import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { marked } from 'marked';
import { generateJSON } from '@tiptap/html';
import StarterKit from '@tiptap/starter-kit';
import TiptapImage from '@tiptap/extension-image';
import TiptapTable from '@tiptap/extension-table';
import TiptapTableRow from '@tiptap/extension-table-row';
import TiptapTableHeader from '@tiptap/extension-table-header';
import TiptapTableCell from '@tiptap/extension-table-cell';
import * as XLSX from 'xlsx';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { useTranslation } from '../../i18n';
import Modal from 'react-modal';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { FaArrowUp, FaFileExcel, FaFilePdf, FaFile, FaChartLine, FaChartBar, FaSpinner, FaLightbulb, FaPaperclip, FaTimes, FaEllipsisV, FaBookmark, FaThumbtack, FaFileAlt, FaPlus, FaCheck } from 'react-icons/fa';

import 'chart.js/auto';
import './chatbotui.css';
import config from '../../amplifyconfiguration.json';
import DataTable from './datatable';
import SQLValidator from '../../utils/SQLValidator';
import {
    buildSuggestedQuestionsPrompt, buildConsultClaudePrompt, buildSQLPrompt
} from '../../utils/prompts';
import FileReferenceDropdown from './FileReferenceDropdown';
import useFileReferences from '../../hooks/useFileReferences';
import useActivityTracker from '../../hooks/useActivityTracker';
import DataTableExport from './DataTableExport';
import { executeSQLDirect, expandSQL, suggestChartTransformation } from '../../utils/strandsClient';

// Cliente para el backend Strands
const STRANDS_API_URL = process.env.REACT_APP_STRANDS_API_URL || 'http://localhost:5001';

// Extensions Tiptap (mismas que usa ReportPage) — para convertir HTML → Tiptap JSON
// al hacer cherry-pick de un mensaje del bot a un reporte
const TIPTAP_EXTENSIONS_FOR_CONVERT = [
  StarterKit,
  TiptapImage.configure({ allowBase64: true }),
  TiptapTable,
  TiptapTableRow,
  TiptapTableHeader,
  TiptapTableCell,
];

// Construye un array de nodos Tiptap a partir de un pendingBlock (message o chart)
function buildTiptapNodes(pendingBlock) {
  if (!pendingBlock) return [];
  if (pendingBlock.type === 'message') {
    // marked() exige un string; si por cualquier razón llega algo que no lo es
    // (p. ej. un elemento React), lo descartamos en vez de romper.
    const rawMd = pendingBlock.content?.markdown;
    const md = typeof rawMd === 'string' ? rawMd : '';
    const html = marked.parse(md, { gfm: true, breaks: false });
    try {
      const doc = generateJSON(html, TIPTAP_EXTENSIONS_FOR_CONVERT);
      return doc.content || [];
    } catch (e) {
      console.error('[CHAT] Error converting markdown to Tiptap:', e);
      // Fallback: nodo de párrafo con el markdown crudo
      return [{ type: 'paragraph', content: [{ type: 'text', text: md }] }];
    }
  }
  if (pendingBlock.type === 'chart') {
    const nodes = [{
      type: 'image',
      attrs: { src: pendingBlock.content?.image_data_url || '', alt: 'Gráfica' }
    }];
    if (pendingBlock.content?.caption) {
      nodes.push({
        type: 'paragraph',
        content: [{ type: 'text', text: pendingBlock.content.caption }]
      });
    }
    return nodes;
  }
  return [];
}

// Normaliza el doc de un reporte. Si es array (legacy) o nulo, devuelve doc vacío.
function normalizeReportDoc(raw) {
  if (!raw || Array.isArray(raw) || typeof raw !== 'object' || raw.type !== 'doc') {
    return { type: 'doc', content: [] };
  }
  return raw;
}

/**
 * Limpia la respuesta del agente removiendo marcadores de formato :::TEXTO:::
 * @param {string} text - Texto a limpiar
 * @returns {string} - Texto limpio
 */
const cleanAgentResponse = (text) => {
    if (!text) return text;
    // Remueve cualquier patrón :::ALGO::: (palabra clave del agente)
    return text.replace(/:::[^:]+:::/g, '').trim();
};

// Componente SVG de estrellas para el icono del bot
const BotStarsIcon = ({ className }) => (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
        {/* Estrella grande central */}
        <path d="M12 5L14 12L12 19L10 12L12 5Z" />
        <path d="M5 12L12 14L19 12L12 10L5 12Z" />
        {/* Estrella arriba derecha */}
        <path d="M20 0L21.5 4.5L20 9L18.5 4.5L20 0Z" />
        <path d="M15.5 4.5L20 6L24 4.5L20 3L15.5 4.5Z" />
        {/* Estrella abajo izquierda */}
        <path d="M4 15L5.5 19.5L4 24L2.5 19.5L4 15Z" />
        <path d="M0 19.5L4 21L8 19.5L4 18L0 19.5Z" />
    </svg>
);


const Chatbotui = ({ isSidebarVisible, userFeatures = {} }) => {

    const { t, language } = useTranslation();
    const { id_conversation } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [response, setResponse] = useState("");
    const { user } = useAuthenticator((context) => [context.user]); // Get the user  const { user } = useAuthenticator((context) => [context.user]); // Get the user
    const id_user = user.username
    const { trackAction } = useActivityTracker(id_user, 'chat');
    const [message, setMessage] = useState('');
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false); // Indicador de carga
    // El estado modalIsOpen (modal legacy de ChartWithControls) fue eliminado.
    const [queryData, setQueryData] = useState(null);
    const messageInputRef = useRef(null);
    const messagesEndRef = useRef(null);
    const [loadingMessage, setLoadingMessage] = useState('Analyzing price trends...'); // Estado para almacenar el mensaje de carga

    // Nuevos estados para manejar la carga en segundo plano de la visualización
    const [vizLoading, setVizLoading] = useState({}); // Estado para seguir qué mensajes están cargando su visualización
    const [vizData, setVizData] = useState({}); // Estado para almacenar datos precargados de visualización por mensaje

    // Estados para el nuevo modal de datos
    const [dataModalOpen, setDataModalOpen] = useState(false);
    const [dataModalMessageId, setDataModalMessageId] = useState(null);

    // Estados para modal de gráfica de tabla
    const [chartModalOpen, setChartModalOpen] = useState(false);
    const [chartModalData, setChartModalData] = useState(null);

    const [activeOptionsMenu, setActiveOptionsMenu] = useState(null);
    const [savingConsult, setSavingConsult] = useState(false);

    // Save to report (cherry-pick) — generalizado para mensajes y graficas
    const [saveReportModal, setSaveReportModal] = useState(null); // { pendingBlock } | null
    const [reportsForSave, setReportsForSave] = useState([]);
    const [loadingReportsForSave, setLoadingReportsForSave] = useState(false);
    const [savingToReport, setSavingToReport] = useState(false);
    const [savedToReportToast, setSavedToReportToast] = useState(null); // { reportId, reportTitle } | null

    /* --------------- NUEVOS STATES Y REFS --------------- */
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [imageModeEnabled, setImageModeEnabled] = useState(false);
    const imageInputRef = useRef(null);

    // Estado para archivos (Excel, PDF, CSV)
    const [uploadedFile, setUploadedFile] = useState(null);

    // Estados para streaming y thinking
    const [streamingMessage, setStreamingMessage] = useState('');
    const [thinkingMessage, setThinkingMessage] = useState('');
    const [showThinking, setShowThinking] = useState(false);
    const [isStreaming, setIsStreaming] = useState(false);
    const abortControllerRef = useRef(null);

    // Hook para referencias de archivos con @
    const {
        availableFiles,
        filteredFiles,
        hasFileAccess,
        referencedFiles,
        showFileDropdown,
        fileSearchText,
        selectedFileIndex,
        detectAtReference,
        updateReferencedFiles,
        insertFileReference,
        fetchFileContent,
        fetchAllReferencedContent,
        handleKeyNavigation,
        setShowFileDropdown,
        setSelectedFileIndex,
        clearReferencedFiles
    } = useFileReferences(id_user, { enabled: userFeatures.fileUpload !== false });

    // Agregar este estado
    const [hoverButtonIndex, setHoverButtonIndex] = useState(null);

    // Suggested questions - initialized from translations
    const [suggestedQuestions, setSuggestedQuestions] = useState([]);

    const [loadingSuggestions, setLoadingSuggestions] = useState(false);

    const endpoint_mes = '/get_messages_conversation';
    const endpoint_add_message = '/add_message';
    const endpoint_get_sql = '/get_query';
    const endpoint_add_saved_consult = '/add_saved_consult';

    const loadingPhrases = [
        "Without data, you're just another person with an opinion. – W. Edwards Deming",
        "In the end, we are all determined by data. – Tim O'Reilly",
        "In God we trust. All others must bring data. – W. Edwards Deming",
        "Data is the new oil. – Clive Humby",
        "The goal is to turn data into information, and information into insight. – Carly Fiorina",
        "Numbers have an important story to tell. They rely on you to give them a clear and convincing voice. – Stephen Few",
        "Torture the data, and it will confess to anything. – Ronald Coase",
        "Data beats emotions. – Sean Rad",
        "You can have data without information, but you cannot have information without data. – Daniel Keys Moran",
        "Big data is at the foundation of all the megatrends that are happening today. – Chris Lynch",
        "Information is the oil of the 21st century, and analytics is the combustion engine. – Peter Sondergaard",
        "The world is one big data problem. – Andrew McAfee",
        "Data is a precious thing and will last longer than the systems themselves. – Tim Berners-Lee",
        "If you torture the data long enough, it will confess to anything. – Darrell Huff",
        "The most valuable commodity I know of is information. – Gordon Gekko (Wall Street)",
        "Data really powers everything that we do. – Jeff Weiner",
    ];

    /* --------------- ---------------  ---------------  Interfaz  ---------------  --------------- --------------- */

    // Función para manejar el clic en el botón de opciones
    const handleOptionsClick = (index, e) => {
        e.stopPropagation(); // Evitar propagación
        setActiveOptionsMenu(activeOptionsMenu === index ? null : index);
    };

    // Cerrar el menú de opciones si se hace clic fuera de él
    useEffect(() => {
        const handleClickOutside = () => {
            setActiveOptionsMenu(null);
        };

        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, []);

    // Initialize suggested questions from translations when language changes
    useEffect(() => {
        const translatedQuestions = t('chat.suggestedQuestions');
        if (Array.isArray(translatedQuestions) && translatedQuestions.length > 0) {
            setSuggestedQuestions(translatedQuestions);
        }
    }, [language, t]);

    // Cambiar la frase 
    useEffect(() => {

        const intervalId = setInterval(() => {
            const randomIndex = Math.floor(Math.random() * loadingPhrases.length);
            setMessages(prevMessages => prevMessages.map(msg =>
                msg.temp ? { ...msg, content: <span className="thinking-dots">{loadingPhrases[randomIndex]}</span> } : msg
            ));
        }, 4000);

        // Limpiar el intervalo cuando se desmonte el componente
        return () => clearInterval(intervalId);
    }, [loadingPhrases]);

    useEffect(() => {
        return () => {
            // Limpiar URL al desmontar el componente
            if (imagePreview) {
                URL.revokeObjectURL(imagePreview);
            }
        };
    }, [imagePreview]);
    // Funciones de input
    const adjustTextareaHeight = (element) => {
        element.style.height = 'auto';
        element.style.height = (element.scrollHeight) + 'px';
    };

    const handleTextareaChange = (e) => {
        const value = e.target.value;
        const cursorPos = e.target.selectionStart;

        setMessage(value);
        adjustTextareaHeight(e.target);

        // Detectar referencias @ para archivos
        detectAtReference(value, cursorPos);

        // Actualizar archivos referenciados
        updateReferencedFiles(value);
    };

    // Manejar teclas especiales para navegacion del dropdown
    const handleTextareaKeyDown = (e) => {
        if (showFileDropdown) {
            const result = handleKeyNavigation(e.key);

            if (result === 'handled') {
                e.preventDefault();
            } else if (result && typeof result === 'object') {
                // Se selecciono un archivo
                e.preventDefault();
                const cursorPos = e.target.selectionStart;
                const { newText, newCursorPosition } = insertFileReference(result, message, cursorPos);
                setMessage(newText);

                // Actualizar inmediatamente los archivos referenciados para la barra de adjuntos
                updateReferencedFiles(newText);

                // Actualizar cursor despues del render
                setTimeout(() => {
                    if (messageInputRef.current) {
                        messageInputRef.current.selectionStart = newCursorPosition;
                        messageInputRef.current.selectionEnd = newCursorPosition;
                    }
                }, 0);
            }
        }
    };

    // Handler para seleccionar archivo desde dropdown
    const handleFileReferenceSelect = (file) => {
        if (messageInputRef.current) {
            const cursorPos = messageInputRef.current.selectionStart;
            const { newText, newCursorPosition } = insertFileReference(file, message, cursorPos);
            setMessage(newText);
            setShowFileDropdown(false);

            // Actualizar inmediatamente los archivos referenciados para la barra de adjuntos
            updateReferencedFiles(newText);

            setTimeout(() => {
                if (messageInputRef.current) {
                    messageInputRef.current.selectionStart = newCursorPosition;
                    messageInputRef.current.selectionEnd = newCursorPosition;
                    messageInputRef.current.focus();
                }
            }, 0);
        }
    };

    // Handler para archivo subido
    const handleFileSelect = (fileData) => {
        setUploadedFile(fileData);
    };

    const handleFileClear = () => {
        if (uploadedFile?.preview) {
            URL.revokeObjectURL(uploadedFile.preview);
        }
        setUploadedFile(null);
    };

    const addMessage = (author, content, type = 'text', data = null, temp = false, attachments = null) => {
        const newMessage = { author, content, type, data, id: Date.now(), temp, attachments };
        setMessages(prevMessages => [...prevMessages, newMessage]);
    };

    // Agregar esta función dentro del componente Chatbotui
    const scrollToBottom = () => {
        // Asegurarse de que messagesEndRef exista y tenga un elemento actual
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    //  Para scroll automático al cargar mensajes iniciales
    useEffect(() => {
        const fetchMessages = async () => {
            if (id_conversation) {
                try {
                    // Código existente para recuperar mensajes...

                    // Después de establecer los mensajes, hacer scroll al final
                    if (response && response.messages) {
                        setMessages(response.messages);
                        // Programar el scroll para después de que los mensajes se rendericen
                        setTimeout(scrollToBottom, 100);
                    } else {
                        console.warn('No messages data in response:', response);
                        setMessages([]);
                    }
                } catch (error) {
                    console.error('Error fetching messages:', error);
                    setMessages([]);
                }
            }
        };

        fetchMessages();
    }, [id_conversation, config]);

    // 3. Agregar un useEffect adicional para monitorear cambios en los mensajes
    useEffect(() => {
        // Hacer scroll hacia abajo cuando cambia la lista de mensajes
        if (messages.length > 0) {
            scrollToBottom();
        }
    }, [messages.length]); // Solo se dispara cuando cambia el número de mensajes

    // Agregar después de tus otros useEffect
    useEffect(() => {
        // Función para manejar el evento de consulta guardada seleccionada
        const handleSavedQueryEvent = (event) => {
            const { query } = event.detail;
            //console.log("Chatbotui: Evento de consulta guardada recibido:", query);

            // Actualizar el estado del mensaje
            setMessage(query);

            // Ajustar la altura del textarea si está disponible
            setTimeout(() => {
                const textarea = messageInputRef.current || document.querySelector('.message-input');
                if (textarea) {
                    textarea.style.height = 'auto';
                    textarea.style.height = (textarea.scrollHeight) + 'px';
                    textarea.focus();
                }
            }, 50);
        };

        // Escuchar el evento personalizado
        document.addEventListener('savedQuerySelected', handleSavedQueryEvent);

        // Limpiar el listener cuando el componente se desmonte
        return () => {
            document.removeEventListener('savedQuerySelected', handleSavedQueryEvent);
        };
    }, []); // Sin dependencias para que se configure una sola vez

    // Si llegamos a /chat/:id navegando desde otra pestana con un pendingQuery
    // (saved consult), pegarlo en el textarea y limpiar el state
    useEffect(() => {
        const pendingQuery = location.state?.pendingQuery;
        if (!pendingQuery) return;

        setMessage(pendingQuery);
        setTimeout(() => {
            const textarea = messageInputRef.current || document.querySelector('.message-input');
            if (textarea) {
                textarea.style.height = 'auto';
                textarea.style.height = textarea.scrollHeight + 'px';
                textarea.focus();
            }
        }, 50);

        // Limpiar el state para que un refresh no vuelva a pegar la consulta
        navigate(location.pathname, { replace: true, state: {} });
    }, [location.state, location.pathname, navigate]);

    // También puedes añadir un useEffect adicional para manejar el cambio de textarea directamente
    useEffect(() => {
        const handleDirectInputChange = (event) => {
            if (event.target.matches('.message-input')) {
                // Si el valor cambió desde fuera de React, sincronizar con el estado
                setMessage(event.target.value);
            }
        };

        // Escuchar cambios de input a nivel de documento
        document.addEventListener('input', handleDirectInputChange);

        return () => {
            document.removeEventListener('input', handleDirectInputChange);
        };
    }, []);

    // Agregar estas funciones
    const handleButtonMouseEnter = (index) => {
        setHoverButtonIndex(index);
    };

    const handleButtonMouseLeave = () => {
        setHoverButtonIndex(null);
    };

    // Este código se debe añadir después de las declaraciones de estado y antes del renderizado, carrusel
    const useDraggableCarousel = () => {
        const carouselRef = useRef(null);
        const isDragging = useRef(false);
        const startX = useRef(0);
        const scrollLeft = useRef(0);

        const handleMouseDown = (e) => {
            if (!carouselRef.current) return;
            isDragging.current = true;
            startX.current = e.pageX - carouselRef.current.offsetLeft;
            scrollLeft.current = carouselRef.current.scrollLeft;
            carouselRef.current.style.cursor = 'grabbing';
        };

        const handleMouseUp = () => {
            if (!carouselRef.current) return;
            isDragging.current = false;
            carouselRef.current.style.cursor = 'grab';
        };

        const handleMouseLeave = () => {
            if (isDragging.current) {
                isDragging.current = false;
                if (carouselRef.current) {
                    carouselRef.current.style.cursor = 'grab';
                }
            }
        };

        const handleMouseMove = (e) => {
            if (!isDragging.current || !carouselRef.current) return;
            e.preventDefault();
            const x = e.pageX - carouselRef.current.offsetLeft;
            const walk = (x - startX.current) * 1.5; // Multiplicador de velocidad
            carouselRef.current.scrollLeft = scrollLeft.current - walk;
        };

        // Manejo de eventos táctiles para dispositivos móviles
        const handleTouchStart = (e) => {
            if (!carouselRef.current) return;
            isDragging.current = true;
            startX.current = e.touches[0].pageX - carouselRef.current.offsetLeft;
            scrollLeft.current = carouselRef.current.scrollLeft;
        };

        const handleTouchMove = (e) => {
            if (!isDragging.current || !carouselRef.current) return;
            const x = e.touches[0].pageX - carouselRef.current.offsetLeft;
            const walk = (x - startX.current) * 1.5;
            carouselRef.current.scrollLeft = scrollLeft.current - walk;
        };

        const handleTouchEnd = () => {
            isDragging.current = false;
        };


        useEffect(() => {
            const carousel = carouselRef.current;
            if (!carousel) return;

            let isDown = false;
            let startX;
            let scrollLeft;

            // Eventos básicos para el arrastre (drag & drop)
            const mouseDown = (e) => {
                isDown = true;
                carousel.style.cursor = 'grabbing';
                startX = e.pageX - carousel.offsetLeft;
                scrollLeft = carousel.scrollLeft;
            };

            const mouseUp = () => {
                isDown = false;
                carousel.style.cursor = 'grab';
            };

            const mouseLeave = () => {
                isDown = false;
                carousel.style.cursor = 'grab';
            };

            const mouseMove = (e) => {
                if (!isDown) return;
                e.preventDefault();
                const x = e.pageX - carousel.offsetLeft;
                const walk = (x - startX) * 1.5; // Velocidad de desplazamiento
                carousel.scrollLeft = scrollLeft - walk;
            };

            // Soporte para dispositivos táctiles
            const touchStart = (e) => {
                isDown = true;
                startX = e.touches[0].pageX - carousel.offsetLeft;
                scrollLeft = carousel.scrollLeft;
            };

            const touchMove = (e) => {
                if (!isDown) return;
                const x = e.touches[0].pageX - carousel.offsetLeft;
                const walk = (x - startX) * 1.5;
                carousel.scrollLeft = scrollLeft - walk;
            };

            const touchEnd = () => {
                isDown = false;
            };

            // Añadir event listeners
            carousel.addEventListener('mousedown', mouseDown);
            carousel.addEventListener('mouseup', mouseUp);
            carousel.addEventListener('mouseleave', mouseLeave);
            carousel.addEventListener('mousemove', mouseMove);

            carousel.addEventListener('touchstart', touchStart);
            carousel.addEventListener('touchmove', touchMove);
            carousel.addEventListener('touchend', touchEnd);

            // Limpieza
            return () => {
                carousel.removeEventListener('mousedown', mouseDown);
                carousel.removeEventListener('mouseup', mouseUp);
                carousel.removeEventListener('mouseleave', mouseLeave);
                carousel.removeEventListener('mousemove', mouseMove);

                carousel.removeEventListener('touchstart', touchStart);
                carousel.removeEventListener('touchmove', touchMove);
                carousel.removeEventListener('touchend', touchEnd);
            };
        }, [suggestedQuestions]);

        return carouselRef;
    };
    const carouselRef = useDraggableCarousel();

    // Manejador para copiar pregunta sugerida al input
    const handleSuggestedQuestion = (question) => {
        setMessage(question);
        if (messageInputRef.current) {
            messageInputRef.current.focus();
            adjustTextareaHeight(messageInputRef.current);
        }
    };

    // Función de procesamiento de archivos (imágenes, Excel, CSV, PDF)
    const handleImageChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validar tamaño (máximo 15MB)
        if (file.size > 15 * 1024 * 1024) {
            alert('El archivo es demasiado grande. Máximo 15MB.');
            return;
        }

        const extension = file.name.split('.').pop()?.toLowerCase();
        const isImage = file.type.startsWith('image/');
        const isExcel = ['xlsx', 'xls', 'xlsm'].includes(extension);
        const isCsv = extension === 'csv';
        const isPdf = extension === 'pdf';

        if (isImage) {
            // Limpiar archivo anterior si existe
            if (imagePreview) {
                URL.revokeObjectURL(imagePreview);
            }
            // Limpiar archivo no-imagen si existe
            if (uploadedFile) {
                handleFileClear();
            }

            setImageFile(file);
            setImagePreview(URL.createObjectURL(file));
            setImageModeEnabled(true);
            messageInputRef.current?.focus();
        } else if (isExcel || isCsv || isPdf) {
            // Limpiar imagen si existe
            if (imagePreview) {
                clearImage();
            }

            try {
                // Convertir a base64
                const base64 = await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => {
                        const result = reader.result;
                        if (typeof result === 'string') {
                            resolve(result.split(',')[1]);
                        } else {
                            reject(new Error('Error al leer archivo'));
                        }
                    };
                    reader.onerror = () => reject(reader.error);
                    reader.readAsDataURL(file);
                });

                // Determinar tipo
                let fileType = 'excel';
                if (isCsv) fileType = 'csv';
                if (isPdf) fileType = 'pdf';

                // Guardar en estado
                handleFileSelect({
                    file,
                    type: fileType,
                    filename: file.name,
                    mimeType: file.type,
                    size: file.size,
                    base64
                });

                messageInputRef.current?.focus();
            } catch (err) {
                console.error('[handleImageChange] Error procesando archivo:', err);
                alert('Error al procesar el archivo. Intenta de nuevo.');
            }
        } else {
            alert('Tipo de archivo no soportado. Usa imágenes, Excel, CSV o PDF.');
        }

        // Limpiar input para poder seleccionar el mismo archivo de nuevo
        e.target.value = '';
    };

    const clearImage = () => {
        // Limpiar URL del objeto para evitar memory leaks
        if (imagePreview) {
            URL.revokeObjectURL(imagePreview);
        }

        setImageFile(null);
        setImagePreview(null);
        setImageModeEnabled(false);

        // Limpiar el input file
        if (imageInputRef.current) {
            imageInputRef.current.value = '';
        }
    };
    /* ---------------  ---------------  --------------- Funciones de mensajes BD ---------------  ---------------  --------------- */

    // ─── Save to Custom Report (cherry-pick) ────────────────────────────
    // Carga reportes (lazy) si no estan en cache
    const ensureReportsLoaded = async () => {
        if (reportsForSave.length > 0) return;
        try {
            setLoadingReportsForSave(true);
            const res = await fetch(`${STRANDS_API_URL}/api/reports?username=${encodeURIComponent(id_user)}`);
            const data = await res.json();
            if (data?.success) setReportsForSave(data.reports || []);
        } catch (err) {
            console.error('[CHAT] Error fetching reports:', err);
        } finally {
            setLoadingReportsForSave(false);
        }
    };

    // Abrir modal con un mensaje del bot como bloque
    const openSaveToReport = async (msg) => {
        // El bloque a guardar necesita el markdown crudo (string). Los mensajes
        // recién generados por el bot guardan un elemento React en `msg.content`
        // y el markdown en `msg.text`; los cargados del historial guardan el
        // string directamente en `msg.content`. Elegimos siempre la variante string.
        const markdown =
            typeof msg.content === 'string'
                ? msg.content
                : (typeof msg.text === 'string' ? msg.text : '');
        setSaveReportModal({
            pendingBlock: {
                type: 'message',
                content: {
                    author: msg.author,
                    markdown,
                    originalMessageId: msg.id_message
                }
            }
        });
        ensureReportsLoaded();
    };

    // Abrir modal con una grafica capturada como bloque
    const openSaveChartToReport = async (payload) => {
        // `payload.type` puede ser 'chart' (default) o 'table'. Ambos se
        // guardan como imagen en el reporte (el frontend ya capturó el
        // canvas o el div como PNG en `image_data_url`). Distinguimos el
        // bloque para que el reporte renderice el rótulo correcto.
        const blockType = payload?.type === 'table' ? 'table' : 'chart';
        setSaveReportModal({
            pendingBlock: {
                type: blockType,
                content: {
                    image_data_url: payload.image_data_url,
                    caption: '',
                    chartType: payload.chartType,
                    labelColumn: payload.labelColumn || '',
                    valueColumn: payload.valueColumn || '',
                    originalMessageId: dataModalMessageId
                }
            }
        });
        ensureReportsLoaded();
    };

    const closeSaveToReport = () => {
        setSaveReportModal(null);
        setSavingToReport(false);
    };

    const saveToExistingReport = async (report) => {
        try {
            setSavingToReport(true);
            // 1) Traer el doc actual del reporte (último estado guardado)
            const getRes = await fetch(`${STRANDS_API_URL}/api/reports/${report.id}?username=${encodeURIComponent(id_user)}`);
            const getData = await getRes.json();
            if (!getData?.success) throw new Error('No se pudo cargar el reporte');

            // 2) Append: nodos nuevos al final de doc.content
            const doc = normalizeReportDoc(getData.report.blocks);
            const newNodes = buildTiptapNodes(saveReportModal.pendingBlock);
            doc.content = [...(doc.content || []), ...newNodes];

            // 3) PATCH del doc completo
            const patchRes = await fetch(`${STRANDS_API_URL}/api/reports/${report.id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username: id_user, blocks: doc })
            });
            const patchData = await patchRes.json();
            if (!patchData?.success) throw new Error('No se pudo guardar');

            closeSaveToReport();
            trackAction('save_to_report', {
                report_id: report.id,
                target: 'existing',
                block_type: saveReportModal.pendingBlock?.type
            });
            setSavedToReportToast({ reportId: report.id, reportTitle: report.title });
            setTimeout(() => setSavedToReportToast(null), 4000);
        } catch (err) {
            console.error('[CHAT] Error saving to report:', err);
            setSavingToReport(false);
        }
    };

    const saveToNewReport = async () => {
        try {
            setSavingToReport(true);
            // 1) Construir doc inicial con los nodos del cherry-pick
            const newNodes = buildTiptapNodes(saveReportModal.pendingBlock);
            const initialDoc = { type: 'doc', content: newNodes };

            // 2) Crear el reporte con el doc ya poblado
            const createRes = await fetch(`${STRANDS_API_URL}/api/reports`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username: id_user, title: 'Nuevo reporte', blocks: initialDoc })
            });
            const createData = await createRes.json();
            if (!createData?.success) throw new Error('No se pudo crear el reporte');
            const newReport = createData.report;

            // 3) Actualizar cache + toast
            setReportsForSave(prev => [
                { id: newReport.id, title: newReport.title, block_count: newNodes.length, created_at: newReport.created_at, updated_at: newReport.updated_at },
                ...prev
            ]);
            closeSaveToReport();
            trackAction('save_to_report', {
                report_id: newReport.id,
                target: 'new',
                block_type: saveReportModal.pendingBlock?.type
            });
            setSavedToReportToast({ reportId: newReport.id, reportTitle: newReport.title });
            setTimeout(() => setSavedToReportToast(null), 4000);

            // 4) Avisar al sidebar para que recargue su lista de reportes
            // (de lo contrario el nuevo reporte solo aparece tras recargar
            // la página). Pasamos el reporte por detail por si el sidebar
            // quiere reaccionar más fino.
            document.dispatchEvent(new CustomEvent('reportCreated', {
                detail: { report: newReport }
            }));
        } catch (err) {
            console.error('[CHAT] Error creating new report:', err);
            setSavingToReport(false);
        }
    };

    // Función para guardar una consulta
    const handleSaveConsult = async (content) => {
        try {
            setSavingConsult(true);
            const url = `${STRANDS_API_URL}${endpoint_add_saved_consult}`;

            const res = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id_user: id_user,
                    consult: content
                })
            });
            const response = await res.json();

            if (response && response.id_saved) {
                // Emitir un evento para notificar que se ha guardado una consulta
                const customEvent = new CustomEvent('querySaved', {
                    detail: { id_saved: response.id_saved },
                    bubbles: true,
                    cancelable: true
                });
                document.dispatchEvent(customEvent);

                // Mostrar confirmación con toast o alert

                setActiveOptionsMenu(null);
            } else {
                throw new Error("No se recibió confirmación del servidor");
            }
        } catch (error) {
            console.error("Error al guardar la consulta:", error);

        } finally {
            setSavingConsult(false);
        }
    };

    // Función para agregar mensaje a la BD
    const addMessageBD = async (id_conversation, id_user, message, author, id_message) => {
        console.log('[addMessageBD] Iniciando:', { author, id_message, id_conversation });
        try {
            // Construir la URL
            const url = `${STRANDS_API_URL}${endpoint_add_message}`;
            console.log('[addMessageBD] URL:', url);

            // Crear AbortController con timeout de 30 segundos
            const controller = new AbortController();
            const timeoutId = setTimeout(() => {
                console.error('[addMessageBD] TIMEOUT - cancelando petición después de 30s');
                controller.abort();
            }, 30000);

            // Hacer la petición POST
            const res = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id_user: id_user,
                    id_conversation: id_conversation,
                    content: message,
                    author: author,
                    id_message: id_message
                }),
                signal: controller.signal
            });

            clearTimeout(timeoutId);
            console.log('[addMessageBD] Response status:', res.status);
            const response = await res.json();
            console.log('[addMessageBD] Response:', response);

            return response.id_message

        } catch (error) {
            if (error.name === 'AbortError') {
                console.error('[addMessageBD] Petición cancelada por timeout');
            } else {
                console.error('[addMessageBD] Error:', error);
            }
            return "None"
        }
    };

    // Función para obtener mensajes
    useEffect(() => {
        const fetchMessages = async () => {
            if (id_conversation) {  // Verificamos que exista id_conversation
                try {
                    // Construir la URL completa con el query parameter
                    const url = `${STRANDS_API_URL}${endpoint_mes}?id_conversation=${encodeURIComponent(id_conversation)}`;

                    // Hacer la petición
                    const res = await fetch(url);
                    const response = await res.json();

                    // Modifica la parte donde seteas los mensajes en el useEffect
                    if (response && response.messages) {
                        // Ordena los mensajes por fecha (asumiendo que tienes un campo created_at o timestamp)
                        const sortedMessages = response.messages.sort((a, b) => {
                            return new Date(a.created_at || a.timestamp || 0) - new Date(b.created_at || b.timestamp || 0);
                        });
                        setMessages(sortedMessages);

                        // 💾 INICIALIZAR vizData con SQL guardado de mensajes del historial
                        const initialVizData = {};
                        sortedMessages.forEach(msg => {
                            // Solo mensajes del Bot que tengan query_sql O chart_suggestion guardados
                            const hasSql = msg.author === 'Bot' && msg.query_sql && msg.id_message;
                            const hasChartSuggestion = msg.author === 'Bot' && msg.chart_suggestion && msg.id_message;
                            if (hasSql || hasChartSuggestion) {
                                if (hasSql) {
                                    console.log('[PERSISTENCIA] SQL encontrado para mensaje:', msg.id_message, msg.query_sql.substring(0, 50) + '...');
                                }
                                if (hasChartSuggestion) {
                                    console.log('[PERSISTENCIA] Sugerencia IA encontrada para mensaje:', msg.id_message);
                                }
                                // Si tenemos chart_suggestion, hidratamos también data/columns
                                // para que la tab "Tabla" muestre los datos agregados sin
                                // necesidad de re-ejecutar SQL ni pasar por /expand-sql
                                // (que devolvía 'SELECT NULL' cuando no había contexto).
                                const cs = msg.chart_suggestion;
                                initialVizData[msg.id_message] = {
                                    data: hasChartSuggestion ? (cs.data || []) : [],
                                    columns: hasChartSuggestion ? (cs.columns || []) : [],
                                    column_types: hasChartSuggestion ? (cs.column_types || {}) : {},
                                    query: msg.query_sql || (hasChartSuggestion ? (cs.sql || '') : ''),
                                    hasError: false,
                                    // Solo recargar si hay SQL real Y no tenemos data ya hidratada
                                    needsLoad: !!msg.query_sql && !hasChartSuggestion,
                                    aiSuggestion: cs || null,
                                };
                            }
                        });

                        if (Object.keys(initialVizData).length > 0) {
                            console.log('[PERSISTENCIA] Inicializando vizData con', Object.keys(initialVizData).length, 'mensajes con SQL');
                            setVizData(prev => ({ ...prev, ...initialVizData }));
                        }
                    } else {
                        console.warn('No messages data in response:', response);
                        setMessages([]);
                    }
                } catch (error) {
                    console.error('Error fetching messages:', error);
                    setMessages([]);
                }
            }
        };

        fetchMessages();
    }, [id_conversation]); // Cargar mensajes cuando cambia la conversación


    // Función para actualizar una consulta
    const updateMessageBD = async (id_message, query_sql) => {
        try {
            // Construir la URL
            const url = `${STRANDS_API_URL}/update_query_sql`;

            // Hacer la petición POST
            const res = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id_message: id_message,
                    query_sql: query_sql
                })
            });
            const response = await res.json();

            return response.message;

        } catch (error) {
            console.error('Error updating query_sql:', error);
            return "Failed to update query_sql";
        }
    };

    /* ---------------  ---------------  --------------- Funciones complementarias (imágenes, preguntas sugeridas, errores)  ---------------  --------------- --------------- */

    // Función de procesamiento de imágenes - Usa backend Strands
    const performImageAnalysis = async (file, userPrompt = '') => {
        console.log('Iniciando análisis de imagen...', {
            fileName: file.name,
            fileSize: file.size,
            fileType: file.type,
            userPrompt: userPrompt
        });

        try {
            // Convertir imagen a base64
            const base64 = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                    try {
                        const result = reader.result;
                        if (typeof result === 'string') {
                            const base64Data = result.split(',')[1];
                            if (!base64Data) {
                                reject(new Error('Error al extraer datos base64'));
                                return;
                            }
                            resolve(base64Data);
                        } else {
                            reject(new Error('Formato de archivo no válido'));
                        }
                    } catch (error) {
                        reject(error);
                    }
                };
                reader.onerror = () => reject(new Error('Error al leer el archivo'));
                reader.readAsDataURL(file);
            });

            // Crear prompt mejorado que incluye el contexto del usuario
            let analysisPrompt = `Analiza esta imagen en el contexto de análisis de datos de negocio. `;

            if (userPrompt && userPrompt.trim()) {
                analysisPrompt += `El usuario quiere: "${userPrompt}". `;
                analysisPrompt += `Describe la imagen de manera que sea útil para responder a esta consulta de negocio. `;
                analysisPrompt += `Si la imagen contiene datos (gráficos, tablas, números), extrae esa información. `;
                analysisPrompt += `Si la imagen muestra procesos de negocio, identifica patrones relevantes. `;
                analysisPrompt += `Proporciona una interpretación que ayude a responder la consulta del usuario.`;
            } else {
                analysisPrompt += `Describe la imagen enfocándote en cualquier información de negocio, datos, `;
                analysisPrompt += `gráficos, tablas, procesos o elementos que puedan ser relevantes para análisis empresarial.`;
            }

            console.log('Enviando solicitud al backend Strands...');

            // Usar el backend Strands para analizar imagen
            const response = await fetch(`${STRANDS_API_URL}/analyze-image`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    image_data: base64,
                    image_type: file.type,
                    prompt: analysisPrompt,
                    username: id_user
                })
            });

            if (!response.ok) {
                throw new Error(`Error HTTP: ${response.status}`);
            }

            const data = await response.json();

            if (!data.success) {
                throw new Error(data.error || 'Error al analizar imagen');
            }

            return data.response;

        } catch (error) {
            console.error('Error completo en análisis de imagen:', error);
            throw error;
        }
    };
    // Nueva función para obtener preguntas sugeridas - Usa backend Strands
    const getSuggestedQuestions = async (previousQuestion, botResponse) => {
        console.log('[SUGERENCIAS-FN] Iniciando con:', {
            previousQuestion: previousQuestion?.substring(0, 50),
            botResponseLen: botResponse?.length
        });

        setLoadingSuggestions(true);

        // Timeout de 30 segundos para dar tiempo al modelo
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000);

        try {
            console.log('[SUGERENCIAS-FN] Enviando fetch a:', `${STRANDS_API_URL}/suggested-questions`);

            const response = await fetch(`${STRANDS_API_URL}/suggested-questions`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    previous_question: previousQuestion,
                    bot_response: botResponse
                }),
                signal: controller.signal
            });

            clearTimeout(timeoutId);
            console.log('[SUGERENCIAS-FN] Response status:', response.status);

            if (!response.ok) {
                console.error('[SUGERENCIAS-FN] Error en el backend:', response.statusText);
                return;
            }

            const data = await response.json();
            console.log('[SUGERENCIAS-FN] Data recibida:', data);

            if (data.success && data.questions && data.questions.length > 0) {
                console.log('[SUGERENCIAS-FN] Actualizando preguntas:', data.questions);
                setSuggestedQuestions(data.questions.slice(0, 3));
            }
        } catch (error) {
            if (error.name === 'AbortError') {
                console.warn('[SUGERENCIAS-FN] Timeout al obtener preguntas sugeridas');
            } else {
                console.error('[SUGERENCIAS-FN] Error:', error);
            }
        } finally {
            clearTimeout(timeoutId);
            setLoadingSuggestions(false);
        }
    };

    // Nueva función para cachar errores - Usa backend Strands
    const consultClaude = async (originalQuery, errorMessage) => {
        const content = buildConsultClaudePrompt(originalQuery, errorMessage);
        try {
            const response = await fetch(`${STRANDS_API_URL}/consult`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    prompt: content,
                    system_prompt: "Eres un asistente experto en analisis de datos que ayuda a resolver errores y proporcionar alternativas.",
                    max_tokens: 1000
                })
            });

            if (!response.ok) {
                console.error('Error en el backend:', response.statusText);
                return null;
            }

            const data = await response.json();
            return data.success ? data.response : null;
        } catch (error) {
            console.error('Error haciendo la petición:', error);
            return null;
        }
    };

    // Función para generar título de conversación desde el primer mensaje
    const generateConversationTitle = async (conversationId, firstMessage) => {
        try {
            console.log('[TITLE] Generando título para conversación:', conversationId);
            const response = await fetch(`${STRANDS_API_URL}/conversations/${conversationId}/generate-title`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    message: firstMessage,
                    username: id_user
                })
            });

            if (!response.ok) {
                console.error('[TITLE] Error HTTP:', response.status);
                return null;
            }

            const data = await response.json();
            if (data.success) {
                console.log('[TITLE] Título generado:', data.title);
                // Emitir evento para que el sidebar actualice la lista de conversaciones
                const titleEvent = new CustomEvent('conversationTitleUpdated', {
                    detail: { conversationId, title: data.title },
                    bubbles: true
                });
                document.dispatchEvent(titleEvent);
                return data.title;
            }
            return null;
        } catch (error) {
            console.error('[TITLE] Error generando título:', error);
            return null;
        }
    };

    /* ---------------  ---------------  --------------- Funciones para el modulo de visualización ---------------  ---------------  --------------- */

    // Parsea la primera tabla markdown del texto del bot. Devuelve
    // {columns, data, column_types} o null si no hay tabla. Útil como
    // fallback del visualizador cuando re-ejecutar SQL falla (columnas
    // alucinadas por expand-sql, schema no accesible, etc.) pero el agente
    // ya había renderizado una tabla con datos válidos en su respuesta.
    const parseMarkdownTableFromText = (text) => {
        if (!text || typeof text !== 'string') return null;
        const lines = text.split(/\r?\n/);
        // Una tabla markdown válida: línea con pipes, siguiente línea con --- entre pipes.
        for (let i = 0; i < lines.length - 1; i++) {
            const header = lines[i].trim();
            const sep = lines[i + 1].trim();
            if (!header.includes('|') || !sep.includes('|')) continue;
            if (!/^\|?\s*:?-{2,}/.test(sep.replace(/\|/g, ' '))) continue;
            const splitRow = (row) => row.replace(/^\||\|$/g, '').split('|').map(c => c.trim());
            const headers = splitRow(header).filter(h => h.length > 0);
            if (headers.length === 0) continue;
            const data = [];
            for (let j = i + 2; j < lines.length; j++) {
                const line = lines[j].trim();
                if (!line.includes('|')) break;
                const cells = splitRow(line);
                if (cells.every(c => c === '')) break;
                const row = {};
                headers.forEach((h, idx) => {
                    let raw = cells[idx] !== undefined ? cells[idx] : '';
                    const cleaned = raw.replace(/[$€£¥,\s]/g, '').replace(/[+%]/g, '');
                    const asNum = parseFloat(cleaned);
                    row[h] = (raw !== '' && !isNaN(asNum) && /^[-+$€£¥]?[\d.,\s]+%?$/.test(raw)) ? asNum : raw;
                });
                data.push(row);
            }
            if (data.length === 0) continue;
            // Tipar columnas: numeric si TODOS los valores son números.
            const column_types = {};
            headers.forEach(h => {
                const allNumeric = data.every(r => typeof r[h] === 'number');
                column_types[h] = allNumeric ? 'double' : 'varchar';
            });
            return { columns: headers, data, column_types };
        }
        return null;
    };

    // Función para cargar la visualización en segundo plano
    // Si hay SQL capturado del agente, lo usa directamente (RÁPIDO)
    // Si no, intenta ejecutar el SQL sin LIMIT
    const loadVisualizationInBackground = async (id_message, capturedSQL, userContent, botContent, rowLimit = 1000) => {
        try {
            console.log('[VIZ-BG] ========== INICIO ==========');
            console.log('[VIZ-BG] Mensaje ID:', id_message);
            console.log('[VIZ-BG] SQL capturado:', capturedSQL ? capturedSQL.substring(0, 80) + '...' : 'NO');
            console.log('[VIZ-BG] Límite de filas:', rowLimit);

            let sqlToExecute = capturedSQL;

            // Si tenemos SQL capturado, ajustar el LIMIT para visualización
            if (sqlToExecute) {
                // Primero remover cualquier LIMIT existente
                sqlToExecute = sqlToExecute.replace(/\s+LIMIT\s+\d+\s*;?\s*$/i, '');
                // Luego añadir el LIMIT solicitado
                sqlToExecute = sqlToExecute.trim();
                if (!sqlToExecute.endsWith(';')) {
                    sqlToExecute += ` LIMIT ${rowLimit}`;
                } else {
                    sqlToExecute = sqlToExecute.slice(0, -1) + ` LIMIT ${rowLimit};`;
                }
                console.log(`[VIZ-BG] SQL con LIMIT ${rowLimit}:`, sqlToExecute.substring(0, 100));
            }

            if (!sqlToExecute) {
                // Sin SQL capturado: intentamos regenerar con Claude vía
                // /expand-sql, usando la respuesta del agente y la pregunta
                // original como contexto. Es transparente para el usuario.
                console.log('[VIZ-BG] Sin SQL capturado; intentando expand-sql con Claude...');
                try {
                    const expandResult = await expandSQL(botContent || '', userContent || '');
                    if (expandResult?.success && expandResult.sql) {
                        const candidate = expandResult.sql.replace(/\s+LIMIT\s+\d+\s*;?\s*$/i, '').trim();
                        // Rechaza SQL placeholders inútiles (Claude a veces
                        // responde con SELECT NULL cuando no entiende el
                        // contexto). Si pasa eso, mejor mostrar "sin datos"
                        // que ejecutar una query basura.
                        const candidateNoSpaces = candidate.replace(/\s+/g, ' ').toUpperCase();
                        const isPlaceholder = (
                            candidateNoSpaces === 'SELECT NULL' ||
                            candidateNoSpaces.startsWith('SELECT NULL ') ||
                            candidateNoSpaces === 'SELECT 1' ||
                            candidateNoSpaces.startsWith('SELECT 1 ') ||
                            !candidateNoSpaces.includes('FROM')
                        );
                        if (isPlaceholder) {
                            console.warn('[VIZ-BG] expand-sql devolvió SQL placeholder, descarto:', candidate);
                        } else {
                            sqlToExecute = candidate;
                            if (!sqlToExecute.endsWith(';')) {
                                sqlToExecute += ` LIMIT ${rowLimit}`;
                            } else {
                                sqlToExecute = sqlToExecute.slice(0, -1) + ` LIMIT ${rowLimit};`;
                            }
                            capturedSQL = candidate;
                            console.log('[VIZ-BG] Claude regeneró SQL:', sqlToExecute.substring(0, 100));
                        }
                    }
                } catch (e) {
                    console.warn('[VIZ-BG] expand-sql falló (no crítico):', e.message);
                }
            }

            // Guard final defensa-en-profundidad: aunque las validaciones
            // anteriores hayan pasado, si sqlToExecute terminó siendo
            // trivial (SELECT NULL, sin FROM, etc.), no lo ejecutamos.
            // Esto cubre el caso del log "SELECT NULL LIMIT 1000" que se
            // reportó: si por alguna razón un SQL placeholder llegó hasta
            // aquí, lo descartamos antes de pegarle a Athena.
            if (sqlToExecute) {
                const upperFinal = sqlToExecute.replace(/\s+/g, ' ').toUpperCase();
                const hasFromTable = /\bFROM\s+(?:"[^"]+"|`[^`]+`|[A-Za-z_][\w.]*)/i.test(sqlToExecute);
                const looksTrivial = (
                    upperFinal.startsWith('SELECT NULL') ||
                    /^SELECT\s+NULL\s+/i.test(sqlToExecute)
                );
                if (!hasFromTable || looksTrivial) {
                    console.warn('[VIZ-BG] Guard final: SQL no usable, descarto:', sqlToExecute);
                    sqlToExecute = null;
                }
            }

            if (!sqlToExecute) {
                console.log('[VIZ-BG] No fue posible obtener SQL ni con Claude');
                setVizData(prev => ({
                    ...prev,
                    [id_message]: {
                        data: [],
                        columns: [],
                        column_types: {},
                        query: '',
                        hasError: true,
                        error: "Los datos no son suficientes para generar una tabla o una gráfica."
                    }
                }));
                setVizLoading(prev => ({ ...prev, [id_message]: false }));
                return;
            }

            // Ejecutar SQL directamente (sin llamar a expand-sql)
            console.log('[VIZ-BG] Ejecutando SQL...');
            const result = await executeSQLDirect(sqlToExecute, null, id_user);
            console.log('[VIZ-BG] Resultado:', result.success ? 'OK' : 'ERROR');

            if (!result.success) {
                throw new Error(result.error || 'Error ejecutando SQL');
            }

            console.log(`[VIZ-BG] Datos cargados: ${result.data?.length || 0} filas`);
            console.log('[VIZ-BG] ========== FIN OK ==========');

            // Guardar datos de visualización con el límite actual
            setVizData(prev => ({
                ...prev,
                [id_message]: {
                    data: result.data || [],
                    columns: result.metadata?.columns || [],
                    column_types: result.metadata?.column_types || {},
                    query: capturedSQL,  // Guardar SQL original sin LIMIT
                    explanation: '',
                    hasError: false,
                    currentLimit: rowLimit  // Guardar el límite actual
                }
            }));

        } catch (error) {
            console.error("[VIZ-BG] ========== ERROR ==========");
            console.error("[VIZ-BG] Error:", error.message);

            // Fallback: si la re-ejecución de SQL falló pero el agente ya había
            // renderizado una tabla en su respuesta, parseamos el markdown y
            // usamos esos datos. Cubre el caso de expand-sql alucinando
            // nombres de columnas que no existen — la data sí estaba en pantalla.
            const parsed = parseMarkdownTableFromText(botContent);
            if (parsed && parsed.data.length > 0) {
                console.log(`[VIZ-BG] Fallback: parseé ${parsed.data.length} filas de la tabla markdown de la respuesta`);
                setVizData(prev => ({
                    ...prev,
                    [id_message]: {
                        data: parsed.data,
                        columns: parsed.columns,
                        column_types: parsed.column_types,
                        query: capturedSQL || prev[id_message]?.query || '',
                        hasError: false,
                        explanation: 'Datos tomados de la tabla mostrada por el agente (no se pudo reejecutar SQL).',
                        currentLimit: rowLimit,
                    }
                }));
                return;
            }

            // El backend devuelve mensajes técnicos ("Error interno del servidor",
            // sintaxis Athena rota, etc.) que no aportan al usuario. Mostramos un
            // mensaje único enfocado en lo que el usuario percibe: la respuesta
            // del agente no tiene datos suficientes para tabular o graficar. El
            // mensaje técnico queda en consola para debugging.
            setVizData(prev => ({
                ...prev,
                [id_message]: {
                    data: [],
                    columns: [],
                    column_types: {},
                    query: capturedSQL || prev[id_message]?.query || '',  // Preservar SQL
                    hasError: true,
                    error: "Los datos no son suficientes para generar una tabla o una gráfica."
                }
            }));
        } finally {
            // Marcar como completado
            setVizLoading(prev => ({
                ...prev,
                [id_message]: false
            }));
        }
    };

    // Función para recargar datos de visualización con límite opcional
    const reloadVisualizationData = async (id_message, rowLimit = 1000) => {
        // Encontrar el mensaje
        const messageIndex = messages.findIndex(msg => msg.id_message === id_message);
        if (messageIndex < 0) return;

        const botContent = messages[messageIndex].content;
        const userMessageIndex = messageIndex > 0 ? messageIndex - 1 : -1;
        const userContent = userMessageIndex >= 0 && messages[userMessageIndex].author === 'Client'
            ? messages[userMessageIndex].content
            : "";

        // Usar SQL ya guardado si existe (para recarga)
        const existingSQL = vizData[id_message]?.query || null;

        console.log('[RELOAD] Recargando con límite:', rowLimit);

        // Marcar como cargando
        setVizLoading(prev => ({ ...prev, [id_message]: true }));

        // Recargar usando SQL existente y nuevo límite
        await loadVisualizationInBackground(id_message, existingSQL, userContent, botContent, rowLimit);
    };

    const getQuery = async (id_message) => {
        try {
            // Construir la URL completa con el query parameter
            const url = `${STRANDS_API_URL}${endpoint_get_sql}?id_message=${encodeURIComponent(id_message)}`;

            // Hacer la petición
            const res = await fetch(url);
            const response = await res.json();
            return response.messages

        } catch (error) {

            console.error('Error getting query:', error);
            return "None"
        }
    };

    // Add this function to your utilities
    function safeJSONStringify(obj) {
        try {
            return JSON.stringify(obj);
        } catch (error) {
            console.warn("Error en serialización JSON:", error);

            // Approach for handling circular references
            const seen = new WeakSet();
            return JSON.stringify(obj, (key, value) => {
                if (typeof value === 'object' && value !== null) {
                    if (seen.has(value)) {
                        return "[Referencia Circular]";
                    }
                    seen.add(value);
                }
                // Handle non-serializable types
                if (typeof value === 'function') {
                    return "[Función]";
                }
                if (value instanceof Error) {
                    return {
                        errorType: value.name,
                        errorMessage: value.message
                    };
                }
                return value;
            }, 2);
        }
    }

    // Genera SQL usando el backend Strands
    const sendMessageToClaude = async (mensajes, userContent, botContent) => {
        const mensajes_form = mensajes.map(({ question, query }) => ({ question, query }));

        try {
            const response = await fetch(`${STRANDS_API_URL}/generate-sql`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    query: userContent,
                    previous_queries: mensajes_form
                })
            });

            if (!response.ok) {
                console.error('Error en el backend:', response.statusText);
                return null;
            }

            const data = await response.json();

            if (data.success) {
                // Retornar en el formato esperado por el codigo existente
                return {
                    content: [{ text: data.sql }]
                };
            }
            return null;
        } catch (error) {
            console.error('Error generando SQL:', error);
            return null;
        }
    };

    const executeSQL = async (sqlStatement) => {
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 120000);

            const response = await fetch(`${STRANDS_API_URL}/execute-sql`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ sql: sqlStatement, username: id_user }),
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            if (!result.success) {
                return { data: [], columns: {}, query: sqlStatement, error: result.error };
            }

            return {
                data: result.data || [],
                columns: result.metadata?.column_types || {},
                query: sqlStatement
            };
        } catch (error) {
            console.error('Error executing SQL query:', error);
            return {
                data: [],
                columns: {},
                query: sqlStatement,
                error: error.name === 'AbortError'
                    ? "La consulta ha excedido el tiempo máximo de ejecución"
                    : error.message || "Error al ejecutar la consulta SQL"
            };
        }
    };

    // Modificado para usar el nuevo modal de datos
    const handleViz = async (id_message) => {
        console.log('[handleViz] Click para mensaje:', id_message);
        trackAction('open_visualizer', { message_id: id_message });

        // Guardar el ID del mensaje y abrir el modal
        setDataModalMessageId(id_message);
        setDataModalOpen(true);

        // Verificar si necesita cargar datos:
        // - No hay datos en vizData
        // - O tiene needsLoad: true (viene del historial con SQL guardado pero sin datos cargados)
        // - O no tiene datos (.data está vacío) y tiene SQL guardado
        const currentVizData = vizData[id_message];
        const hasRealSql = !!(currentVizData && currentVizData.query);
        const hasAiSuggestion = !!(currentVizData && currentVizData.aiSuggestion);

        // Caso especial: mensaje sin SQL real pero CON sugerencia IA persistida.
        // Antes intentábamos /expand-sql, que generaba placeholders inútiles
        // (`SELECT NULL`) cuando Claude no podía inferir SQL. Ahora skipeamos:
        // el modal abre directo con la gráfica IA hidratada.
        if (!hasRealSql && hasAiSuggestion) {
            console.log('[handleViz] Mensaje solo con sugerencia IA — skip reload, abrir directo en gráfica');
            return;
        }

        const needsDataLoad = !currentVizData ||
                              currentVizData.needsLoad ||
                              (currentVizData.query && (!currentVizData.data || currentVizData.data.length === 0));

        if (needsDataLoad && !vizLoading[id_message]) {
            console.log('[handleViz] Iniciando carga de datos para:', id_message);
            await reloadVisualizationData(id_message);
        }
    };

    // Cerrar modal de datos
    const handleCloseDataModal = () => {
        setDataModalOpen(false);
        setDataModalMessageId(null);
    };


    // Función para extraer datos de tabla markdown y descargar como Excel
    const handleDownloadTableAsExcel = (tableElement) => {
        try {
            // Extraer datos de la tabla
            const rows = tableElement.querySelectorAll('tr');
            const data = [];

            rows.forEach((row, index) => {
                const cells = row.querySelectorAll('th, td');
                const rowData = {};

                cells.forEach((cell, cellIndex) => {
                    const columnName = index === 0 ?
                        cell.textContent.trim() :
                        `Column_${cellIndex + 1}`;

                    if (index === 0) {
                        // Si es la primera fila (headers), usar como nombres de columnas
                        rowData[columnName] = columnName;
                    } else {
                        // Para las demás filas, usar los headers de la primera fila
                        const headers = Array.from(tableElement.querySelectorAll('tr:first-child th, tr:first-child td'));
                        const headerText = headers[cellIndex]?.textContent.trim() || `Column_${cellIndex + 1}`;
                        rowData[headerText] = cell.textContent.trim();
                    }
                });

                if (index > 0) { // Skip header row for data
                    data.push(rowData);
                }
            });

            // Generar timestamp
            const timestamp = new Date().toISOString().replace(/[-:]/g, '').replace(/\..+/, '');

            // Crear y descargar Excel
            const worksheet = XLSX.utils.json_to_sheet(data);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Tabla');
            XLSX.writeFile(workbook, `tabla_${timestamp}.xlsx`);

        } catch (error) {
            console.error('Error al descargar tabla:', error);
            alert('Error al generar el archivo Excel');
        }
    };

    const handleGenerateExcel = () => {
        if (!queryData || !queryData.data) {
            console.error('No hay datos disponibles para generar el Excel.');
            return;
        }

        // Obtener timestamp
        const timestamp = new Date().toISOString().replace(/[-:]/g, '').replace(/\..+/, '');

        // Generar el archivo Excel
        const worksheet = XLSX.utils.json_to_sheet(queryData.data); // Convierte los datos a formato Excel
        const workbook = XLSX.utils.book_new(); // Crea un nuevo libro
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Datos'); // Agrega la hoja de datos
        XLSX.writeFile(workbook, `resultado_${timestamp}.xlsx`); // Descarga el archivo con timestamp
    };

    /* --------------- ---------------  ---------------  Petición de bedrock --------------- ---------------  ---------------  */

    // Funcion para invocar el backend Strands (Claude via Bedrock)
    const invokeStrandsAgent = async (prompt, sessionId, id_message_bot, fileData = null) => {
        console.log("[STRANDS] Enviando query al backend local:", STRANDS_API_URL);

        try {
            // Construir payload
            const payload = {
                query: prompt,
                username: id_user,
                language: language, // Enviar idioma para respuesta correcta
                conversation_history: messages.slice(-20).map(m => ({
                    role: m.author === 'Client' ? 'user' : 'assistant',
                    content: typeof m.content === 'string' ? m.content : ''
                }))
            };

            // Agregar datos de archivo si existen
            if (fileData) {
                switch (fileData.type) {
                    case 'excel':
                    case 'csv':
                        payload.excel_data = fileData.base64;
                        payload.excel_filename = fileData.filename;
                        console.log(`[STRANDS] Archivo ${fileData.type.toUpperCase()} incluido: ${fileData.filename}`);
                        break;
                    case 'pdf':
                        payload.pdf_data = fileData.base64;
                        payload.pdf_filename = fileData.filename;
                        console.log(`[STRANDS] Archivo PDF incluido: ${fileData.filename}`);
                        break;
                    case 'image':
                        payload.image_data = fileData.base64;
                        payload.image_mime_type = fileData.mimeType;
                        payload.image_filename = fileData.filename;
                        console.log(`[STRANDS] Imagen incluida: ${fileData.filename}`);
                        break;
                    default:
                        console.log(`[STRANDS] Tipo de archivo no soportado: ${fileData.type}`);
                }
            }

            const response = await fetch(`${STRANDS_API_URL}/query`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || `HTTP ${response.status}`);
            }

            const data = await response.json();
            console.log("[STRANDS] Respuesta recibida:", data);

            return {
                completion: data.response || data.content || '',
                runningTraces: [],
                tokenUsage: {
                    totalInputTokens: 0,
                    totalOutputTokens: 0,
                    totalTokens: 0
                }
            };
        } catch (error) {
            console.error("[STRANDS] Error:", error);
            throw error;
        }
    };

    // Funcion principal para invocar al agente usando SSE (Server-Sent Events) real
    const invokeStrandsAgentStream = async (prompt, fileData = null, referencedFilesData = []) => {
        console.log("[STRANDS-SSE] Iniciando streaming a:", STRANDS_API_URL);
        console.log("[STRANDS-SSE] 📥 Parámetros recibidos:");
        console.log("  - prompt:", prompt?.substring(0, 50) + "...");
        console.log("  - fileData:", fileData ? `${fileData.type}/${fileData.filename}` : "null");
        console.log("  - referencedFilesData:", {
            isArray: Array.isArray(referencedFilesData),
            length: referencedFilesData?.length || 0,
            files: referencedFilesData?.map(f => ({
                name: f.original_name,
                type: f.file_type,
                hasContent: !!f.content_base64
            })) || []
        });

        // Crear AbortController para poder cancelar
        abortControllerRef.current = new AbortController();

        // Flag para evitar resolver la promesa múltiples veces
        let isResolved = false;

        return new Promise((resolve, reject) => {
            const safeResolve = (value) => {
                if (!isResolved) {
                    isResolved = true;
                    resolve(value);
                }
            };

            const safeReject = (error) => {
                if (!isResolved) {
                    isResolved = true;
                    reject(error);
                }
            };

            try {
                // Construir payload
                const payload = {
                    query: prompt,
                    username: id_user,
                    language: language, // Enviar idioma para respuesta correcta
                    conversation_history: messages.slice(-20).map(m => ({
                        role: m.author === 'Client' ? 'user' : 'assistant',
                        content: typeof m.content === 'string' ? m.content : (m.text || '')
                    }))
                };

                // Agregar archivos referenciados con @
                console.log('[STRANDS-SSE] 🔍 Verificando referencedFilesData:', {
                    exists: !!referencedFilesData,
                    isArray: Array.isArray(referencedFilesData),
                    length: referencedFilesData?.length || 0
                });

                if (referencedFilesData && referencedFilesData.length > 0) {
                    console.log('[STRANDS-SSE] ✅ Procesando', referencedFilesData.length, 'archivos referenciados');
                    for (let i = 0; i < referencedFilesData.length; i++) {
                        const refFile = referencedFilesData[i];
                        console.log(`[STRANDS-SSE] Archivo ${i + 1}:`, {
                            name: refFile.original_name,
                            type: refFile.file_type,
                            hasContent: !!refFile.content_base64,
                            contentLength: refFile.content_base64?.length || 0,
                            allKeys: Object.keys(refFile)
                        });

                        if (refFile.content_base64) {
                            console.log(`[STRANDS-SSE] 📄 ${refFile.original_name}: tipo=${refFile.file_type}, base64=${refFile.content_base64.length} chars`);
                            if (refFile.file_type === 'csv' || refFile.file_type === 'excel') {
                                payload.excel_data = refFile.content_base64;
                                payload.excel_filename = refFile.original_name;
                                console.log(`[STRANDS-SSE] ✅✅ AGREGADO como excel_data: ${refFile.original_name} (${refFile.content_base64.length} chars)`);
                            } else if (refFile.file_type === 'pdf') {
                                payload.pdf_data = refFile.content_base64;
                                payload.pdf_filename = refFile.original_name;
                                console.log(`[STRANDS-SSE] ✅✅ AGREGADO como pdf_data: ${refFile.original_name}`);
                            } else if (refFile.file_type === 'image') {
                                payload.image_data = refFile.content_base64;
                                payload.image_mime_type = refFile.mime_type || 'image/png';
                                payload.image_filename = refFile.original_name;
                                console.log(`[STRANDS-SSE] ✅✅ AGREGADO como image_data: ${refFile.original_name}`);
                            } else {
                                console.warn(`[STRANDS-SSE] ⚠️ Tipo de archivo no soportado: ${refFile.file_type}`);
                            }
                        } else {
                            console.warn(`[STRANDS-SSE] ⚠️ Archivo ${refFile.original_name} sin content_base64`);
                        }
                    }
                } else {
                    console.log('[STRANDS-SSE] ⚠️ No hay archivos referenciados para procesar');
                }

                // Agregar archivo subido directamente
                if (fileData) {
                    switch (fileData.type) {
                        case 'excel':
                        case 'csv':
                            payload.excel_data = fileData.base64;
                            payload.excel_filename = fileData.filename;
                            break;
                        case 'pdf':
                            payload.pdf_data = fileData.base64;
                            payload.pdf_filename = fileData.filename;
                            break;
                        case 'image':
                            payload.image_data = fileData.base64;
                            payload.image_mime_type = fileData.mimeType;
                            payload.image_filename = fileData.filename;
                            break;
                        default:
                            break;
                    }
                }

                // Iniciar estados de streaming
                setIsStreaming(true);
                setShowThinking(true);
                setThinkingMessage('Conectando...');
                setStreamingMessage('');

                let accumulatedResponse = '';
                let finalResponseStarted = false;

                // 🔍 LOG del payload final antes de enviar
                console.log('[STRANDS-SSE] 📤 Payload final a enviar:', {
                    query: payload.query?.substring(0, 50) + '...',
                    username: payload.username,
                    hasExcelData: !!payload.excel_data,
                    excelFilename: payload.excel_filename || 'N/A',
                    excelDataLength: payload.excel_data?.length || 0,
                    hasPdfData: !!payload.pdf_data,
                    hasImageData: !!payload.image_data,
                    conversationHistoryLength: payload.conversation_history?.length || 0
                });

                // Usar fetch con streaming para SSE (mejor compatibilidad que EventSource para POST)
                fetch(`${STRANDS_API_URL}/query-stream`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'text/event-stream'
                    },
                    body: JSON.stringify(payload),
                    signal: abortControllerRef.current.signal
                })
                .then(response => {
                    if (!response.ok) {
                        throw new Error(`HTTP ${response.status}`);
                    }

                    const reader = response.body.getReader();
                    const decoder = new TextDecoder();
                    let buffer = '';

                    // Throttle para pensamientos - actualizar máximo cada 150ms
                    let lastThinkingUpdate = 0;
                    let pendingThinkingText = '';
                    const THINKING_UPDATE_INTERVAL = 150;

                    const updateThinkingThrottled = (text) => {
                        pendingThinkingText = text;
                        const now = Date.now();
                        if (now - lastThinkingUpdate >= THINKING_UPDATE_INTERVAL) {
                            setThinkingMessage(pendingThinkingText);
                            lastThinkingUpdate = now;
                        }
                    };

                    const processStream = async () => {
                        try {
                            while (true) {
                                const { done, value } = await reader.read();

                                if (done) {
                                    console.log('[STRANDS-SSE] Stream finalizado');
                                    // Asegurar que el último pensamiento se muestre
                                    if (pendingThinkingText) {
                                        setThinkingMessage(pendingThinkingText);
                                    }
                                    break;
                                }

                                // Decodificar chunk y agregar al buffer
                                buffer += decoder.decode(value, { stream: true });

                                // Procesar lineas completas del buffer
                                const lines = buffer.split('\n');
                                buffer = lines.pop() || ''; // Guardar linea incompleta

                                for (const line of lines) {
                                    if (line.startsWith('data: ')) {
                                        try {
                                            const jsonStr = line.slice(6); // Remover "data: "
                                            if (!jsonStr.trim()) continue;

                                            const event = JSON.parse(jsonStr);
                                            console.log('[STRANDS-SSE] Evento:', event.type);

                                            switch (event.type) {
                                                case 'start':
                                                    console.log('[STRANDS-SSE] Conexion iniciada');
                                                    break;

                                                case 'thinking':
                                                    // Mostrar mensaje de thinking (lifecycle) - throttled
                                                    updateThinkingThrottled(event.content || 'Procesando...');
                                                    break;

                                                case 'reasoning':
                                                    // Texto de razonamiento (Extended Thinking de Claude) - throttled
                                                    console.log('[STRANDS-SSE] Reasoning:', event.content?.substring(0, 50));
                                                    updateThinkingThrottled(event.content || 'Razonando...');
                                                    break;

                                                case 'thinking_stream':
                                                    // Texto de pensamiento del agente - throttled para velocidad legible
                                                    console.log('[STRANDS-SSE] Thinking stream:', event.content?.substring(0, 50));
                                                    if (event.content) {
                                                        updateThinkingThrottled(event.content);
                                                    }
                                                    break;

                                                case 'tool_use':
                                                    // Mostrar que se esta usando una herramienta - inmediato
                                                    console.log('[STRANDS-SSE] Tool use:', event.tool, '#', event.count);
                                                    setThinkingMessage(event.content || `Usando: ${event.tool}`);
                                                    break;

                                                case 'tool_complete':
                                                    // Herramienta completada - inmediato
                                                    console.log('[STRANDS-SSE] Tool complete #', event.count);
                                                    setThinkingMessage('Procesando resultados...');
                                                    break;

                                                case 'final_response_start':
                                                    // Ocultar thinking y preparar para respuesta
                                                    console.log('[STRANDS-SSE] Final response starting');
                                                    finalResponseStarted = true;
                                                    setShowThinking(false);
                                                    setThinkingMessage('');
                                                    accumulatedResponse = '';
                                                    break;

                                                case 'chunk':
                                                    // Agregar chunk a la respuesta
                                                    if (event.content) {
                                                        // Si no se ha marcado el inicio, activarlo automaticamente
                                                        if (!finalResponseStarted) {
                                                            console.log('[STRANDS-SSE] Activando respuesta final (chunk sin final_response_start)');
                                                            finalResponseStarted = true;
                                                            setShowThinking(false);
                                                            setThinkingMessage('');
                                                        }
                                                        accumulatedResponse += event.content;
                                                        setStreamingMessage(cleanAgentResponse(accumulatedResponse));
                                                    }
                                                    break;

                                                case 'done':
                                                    // Stream completado - GARANTIZAR que thinking se oculte
                                                    console.log('[STRANDS-SSE] Respuesta completa:', accumulatedResponse.length, 'caracteres');
                                                    console.log('[STRANDS-SSE] SQL capturado:', event.captured_sql ? event.captured_sql.substring(0, 100) : 'ninguno');
                                                    setIsStreaming(false);
                                                    setShowThinking(false);
                                                    setThinkingMessage('');  // Limpiar mensaje de thinking

                                                    // IMPORTANTE: Cerrar la conexión COMPLETAMENTE
                                                    // 1. Cancelar el reader
                                                    reader.cancel().catch(() => {});
                                                    // 2. Abortar el AbortController para forzar cierre de conexión HTTP
                                                    if (abortControllerRef.current) {
                                                        abortControllerRef.current.abort();
                                                        abortControllerRef.current = null;
                                                    }
                                                    console.log('[STRANDS-SSE] Conexión cerrada completamente');

                                                    // Capturar resultado antes de resolver
                                                    const result = {
                                                        completion: accumulatedResponse || event.content || '',
                                                        success: true,
                                                        captured_sql: event.captured_sql || null,
                                                        all_sql: event.all_sql || [],
                                                        input_tokens: event.input_tokens || 0,
                                                        output_tokens: event.output_tokens || 0,
                                                    };
                
                                                    // Pequeño delay para asegurar que la conexión se cierre
                                                    setTimeout(() => {
                                                        safeResolve(result);
                                                    }, 100);
                                                    return;

                                                case 'error':
                                                    console.error('[STRANDS-SSE] Error del servidor:', event.error);
                                                    setIsStreaming(false);
                                                    setShowThinking(false);
                                                    setThinkingMessage('');  // Limpiar mensaje de thinking
                                                    setStreamingMessage('');
                                                    // Cerrar conexión completamente
                                                    reader.cancel().catch(() => {});
                                                    if (abortControllerRef.current) {
                                                        abortControllerRef.current.abort();
                                                        abortControllerRef.current = null;
                                                    }
                                                    safeReject(new Error(event.error || 'Error en el servidor'));
                                                    return;

                                                default:
                                                    console.log('[STRANDS-SSE] Evento desconocido:', event.type, event);
                                            }
                                        } catch (parseError) {
                                            console.warn('[STRANDS-SSE] Error parseando evento:', parseError);
                                        }
                                    }
                                }
                            }

                            // Si llegamos aqui sin resolver, usar la respuesta acumulada
                            // GARANTIZAR que thinking se oculte al finalizar
                            setShowThinking(false);
                            setThinkingMessage('');
                            setIsStreaming(false);

                            // Limpiar AbortController
                            if (abortControllerRef.current) {
                                abortControllerRef.current = null;
                            }

                            if (accumulatedResponse) {
                                safeResolve({
                                    completion: accumulatedResponse,
                                    success: true
                                });
                            }

                        } catch (streamError) {
                            // GARANTIZAR que thinking se oculte en error
                            setShowThinking(false);
                            setThinkingMessage('');
                            setIsStreaming(false);

                            // Limpiar AbortController
                            if (abortControllerRef.current) {
                                abortControllerRef.current = null;
                            }

                            if (streamError.name === 'AbortError') {
                                console.log('[STRANDS-SSE] Stream cancelado (abort esperado)');
                                setStreamingMessage('');
                                safeResolve({ completion: '', success: false, cancelled: true });
                            } else {
                                safeReject(streamError);
                            }
                        }
                    };

                    processStream();
                })
                .catch(error => {
                    if (error.name === 'AbortError') {
                        console.log('[STRANDS-SSE] Conexión cerrada (esperado después de done)');
                        // No resolvemos aquí porque ya se resolvió en el caso 'done'
                    } else {
                        console.error("[STRANDS-SSE] Error:", error);
                        setIsStreaming(false);
                        setShowThinking(false);
                        setStreamingMessage('');
                        safeReject(error);
                    }
                });

            } catch (error) {
                console.error("[STRANDS-SSE] Error inicial:", error);
                setIsStreaming(false);
                setShowThinking(false);
                setStreamingMessage('');
                safeReject(error);
            }
        });
    };

    const handleInvokeAgent = async (message, sessionId, id_message_bot, fileData = null, referencedFilesData = []) => {
        console.log('[handleInvokeAgent] 📤 Llamando con:', {
            messageLength: message?.length || 0,
            fileData: fileData ? `${fileData.type}/${fileData.filename}` : 'null',
            referencedFilesData: {
                length: referencedFilesData?.length || 0,
                files: referencedFilesData?.map(f => ({
                    name: f.original_name,
                    hasContent: !!f.content_base64,
                    contentLength: f.content_base64?.length || 0
                })) || []
            }
        });
        try {
            // Usar streaming SSE para mejor experiencia
            const result = await invokeStrandsAgentStream(message, fileData, referencedFilesData);

            if (result.cancelled) {
                return { completion: null, cancelled: true };
            }

            setResponse(result.completion);

            // Retornar información para que handleSubmit maneje la secuencia
            // NO iniciamos visualización aquí - lo hará handleSubmit de forma secuencial
            return {
                completion: result.completion,
                captured_sql: result.captured_sql || null,
                id_message_bot: id_message_bot,
                message: message
            };
        } catch (error) {
            console.error("Error invoking agent:", error);
            throw error;
        }
    };

    // Componente separado para tabla con descarga y gráfica
    const MarkdownTableWithDownload = ({ children }) => {
        const tableRef = useRef(null);

        const handleDownloadTable = () => {
            console.log("Descargando tabla...");
            handleDownloadTableAsExcel(tableRef.current);
        };

        // Extraer datos de la tabla HTML para gráfica
        const extractTableData = () => {
            if (!tableRef.current) return null;

            const table = tableRef.current;
            const headers = [];
            const rows = [];

            // Obtener headers
            const headerCells = table.querySelectorAll('thead th');
            headerCells.forEach(th => headers.push(th.textContent.trim()));

            // Obtener filas de datos
            const bodyRows = table.querySelectorAll('tbody tr');
            bodyRows.forEach(tr => {
                const row = {};
                const cells = tr.querySelectorAll('td');
                cells.forEach((td, idx) => {
                    if (headers[idx]) {
                        let value = td.textContent.trim();
                        // Limpiar caracteres de formato monetario y separadores
                        const cleanedValue = value.replace(/[$€£¥,\s]/g, '');
                        // Intentar convertir a número si es posible
                        const numValue = parseFloat(cleanedValue);
                        row[headers[idx]] = isNaN(numValue) ? value : numValue;
                    }
                });
                rows.push(row);
            });

            return { headers, data: rows };
        };

        const handleOpenChart = () => {
            const tableData = extractTableData();
            if (tableData && tableData.data.length > 0) {
                setChartModalData(tableData);
                setChartModalOpen(true);
            }
        };

        return (
            <div className="markdown-table-wrapper">
                <div className="table-header">
                    <button
                        className="chart-table-btn"
                        onClick={handleOpenChart}
                        title={t('chat.generateChart')}
                    >
                        <FaChartBar size={12} />
                        {t('chat.chart')}
                    </button>
                    {userFeatures.dataExport !== false && (
                    <button
                        className="download-table-btn"
                        onClick={handleDownloadTable}
                        title={t('chat.downloadTableAsExcel')}
                    >
                        <FaFileExcel size={12} />
                        {t('chat.download')}
                    </button>
                    )}
                </div>
                <div className="markdown-table-container">
                    <table className="markdown-table" ref={tableRef}>
                        {children}
                    </table>
                </div>
            </div>
        );
    };
    /* --------------- Función enviar formulario --------------- */
    // FUNCIÓN HANDLESUBMIT CORREGIDA - Reemplazar la función completa

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!message.trim()) return;

        trackAction('send_query');

        let finalQuery = message;
        let imageAnalysisContext = "";
        let firstAgentMessageId = null;
        let bedrockMessageId = null;

        // Flag para rastrear si hubo procesamiento de imagen
        let hasImageProcessing = false;

        // 🔧 FIX: Re-ejecutar updateReferencedFiles para asegurar valores actualizados
        // Esto soluciona problemas de timing donde el estado no se ha actualizado aún
        const currentReferencedFiles = updateReferencedFiles(message);
        console.log('[handleSubmit] 📎 Archivos referenciados actualizados:', currentReferencedFiles?.map(f => f.original_name) || []);

        // Capturar si es el primer mensaje (antes de agregar el nuevo)
        const isFirstMessage = messages.filter(m => m.author === 'Client').length === 0;

        // Capturar archivos adjuntos ANTES de limpiarlos (para mostrar en burbuja)
        // Convertir imagePreview de blob URL a base64 data URL para que persista
        let imagePreviewBase64 = null;
        if (imageFile && imagePreview) {
            try {
                // Convertir blob URL a base64 para persistencia
                const response = await fetch(imagePreview);
                const blob = await response.blob();
                imagePreviewBase64 = await new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result);
                    reader.readAsDataURL(blob);
                });
            } catch (e) {
                console.warn('Error converting image to base64 for display:', e);
                imagePreviewBase64 = imagePreview; // Fallback to blob URL
            }
        }

        const currentAttachments = {
            uploadedFile: uploadedFile ? { ...uploadedFile } : null,
            imagePreview: imagePreviewBase64 || imagePreview || null,
            referencedFiles: currentReferencedFiles ? [...currentReferencedFiles] : []
        };

        if (imageFile) {
            console.log('Procesando imagen para análisis...');
            hasImageProcessing = true;

            try {
                // Agregar mensaje del cliente con archivos adjuntos
                addMessage('Client', message, 'text', null, false, currentAttachments);
                setMessage('');
                adjustTextareaHeight(messageInputRef.current);

                // Crear ID para el mensaje de análisis de imagen
                firstAgentMessageId = Date.now();

                // Mostrar mensaje de análisis de imagen
                const imageAnalysisMessage = {
                    author: 'Bot',
                    content: (
                        <div className="image-analysis-progress">
                            <span className="thinking-dots">
                                🔍 Analizando imagen...
                            </span>
                            <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
                                Extrayendo información relevante de la imagen
                            </div>
                        </div>
                    ),
                    type: 'text',
                    data: null,
                    id: firstAgentMessageId,
                    temp: true,
                    isImageAnalysisMessage: true,
                    isPreviewMessage: true // 🔧 MARCAR COMO PREVIEW
                };
                setMessages(prevMessages => [...prevMessages, imageAnalysisMessage]);

                // 🔧 MEJORAR SCROLL
                setTimeout(() => {
                    scrollToBottom();
                }, 200);

                // Guardar referencias antes de limpiar
                const imageToAnalyze = imageFile;
                const userPrompt = message.trim();

                // Limpiar imagen después de mostrar el mensaje
                clearImage();

                // Realizar análisis de imagen
                imageAnalysisContext = await performImageAnalysis(imageToAnalyze, userPrompt);

                // Verificar si el análisis de imagen fue exitoso
                if (imageAnalysisContext && !imageAnalysisContext.includes("Error")) {
                    finalQuery = `INFORMACIÓN EXTRAÍDA DE LA IMAGEN:
                ${imageAnalysisContext}

                CONSULTA ORIGINAL DEL USUARIO:
                ${userPrompt}

                Por favor, utiliza la información extraída de la imagen junto con nuestros datos internos para proporcionar una respuesta completa y relevante a la consulta del usuario.`;
                } else {
                    finalQuery = userPrompt;
                    imageAnalysisContext = "";
                }

                // FINALIZAR el mensaje de análisis de imagen
                setMessages(prevMessages =>
                    prevMessages.map(msg =>
                        msg.id === firstAgentMessageId ? {
                            ...msg,
                            content: (
                                <div>
                                    {imageAnalysisContext && !imageAnalysisContext.includes("Error") ? (
                                        <div className="image-info-card">
                                            <strong>🔍 Imagen analizada correctamente</strong>
                                            <div className="image-info-content">
                                                {imageAnalysisContext}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="image-analysis-error">
                                            <strong>⚠️ Error al analizar imagen</strong>
                                            <div style={{ marginTop: "5px", fontSize: "0.9em" }}>
                                                Continuando con análisis de datos internos...
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ),
                            temp: false,
                            isImageAnalysisMessage: true,
                            isPreviewMessage: true // 🔧 MANTENER COMO PREVIEW
                        } : msg
                    )
                );

                // 🔧 SCROLL DESPUÉS DE FINALIZAR
                setTimeout(() => {
                    scrollToBottom();
                }, 200);

            } catch (imageError) {
                console.error('Error en análisis de imagen:', imageError);

                // Finalizar mensaje de análisis con error
                setMessages(prevMessages =>
                    prevMessages.map(msg =>
                        msg.id === firstAgentMessageId ? {
                            ...msg,
                            content: (
                                <div className="image-analysis-error">
                                    <strong>⚠️ No se pudo analizar la imagen</strong>
                                    <div style={{ marginTop: "5px", fontSize: "0.9em" }}>
                                        Continuando con análisis de datos internos...
                                    </div>
                                </div>
                            ),
                            temp: false,
                            isImageAnalysisMessage: true,
                            isPreviewMessage: true // 🔧 MARCAR COMO PREVIEW
                        } : msg
                    )
                );

                finalQuery = message;
            }

        } else {
            // Flujo normal sin imagen
            addMessage('Client', message, 'text', null, false, currentAttachments);
            setMessage('');
            adjustTextareaHeight(messageInputRef.current);
        }

        // 🔥 CREAR NUEVO MENSAJE PARA BEDROCK
        bedrockMessageId = Date.now() + 1;

        // Mostrar placeholder inmediatamente mientras se prepara la conexión
        setShowThinking(true);
        setThinkingMessage(loadingPhrases[Math.floor(Math.random() * loadingPhrases.length)]);

        // 🔧 SCROLL MEJORADO
        setTimeout(() => {
            scrollToBottom();
        }, 200);

        // PROCESAR BEDROCK Y RESPUESTA FINAL
        try {
            let id_message_client = uuidv4();
            let response_message_client = await addMessageBD(id_conversation, id_user, message, "Client", id_message_client);

            let id_message_bot = uuidv4();

            // Obtener contenido de archivos referenciados con @
            // MÉTODO DIRECTO: Buscar archivos en el mensaje y obtener su contenido
            let referencedFilesContent = [];

            console.log('[CHAT] 📎 Buscando archivos referenciados en mensaje:', message);
            console.log('[CHAT] 📁 Archivos disponibles:', availableFiles?.length || 0);

            // Buscar archivos referenciados directamente en el mensaje (más robusto)
            if (message?.includes('@') && availableFiles && availableFiles.length > 0) {
                const filesInMessage = [];
                for (const file of availableFiles) {
                    if (file.original_name && message.includes(`@${file.original_name}`)) {
                        console.log(`[CHAT] ✅ Archivo encontrado en mensaje: ${file.original_name}`);
                        filesInMessage.push(file);
                    }
                }

                console.log(`[CHAT] 📎 Total archivos encontrados: ${filesInMessage.length}`);

                // Obtener contenido de cada archivo
                if (filesInMessage.length > 0) {
                    for (const file of filesInMessage) {
                        console.log(`[CHAT] 📥 Obteniendo contenido de: ${file.original_name} (ID: ${file.id})`);
                        const content = await fetchFileContent(file.id);
                        if (content) {
                            const merged = { ...file, ...content };
                            console.log(`[CHAT] ✅ Contenido obtenido:`, {
                                name: merged.original_name,
                                file_type: merged.file_type,
                                hasContent: !!merged.content_base64,
                                contentLength: merged.content_base64?.length || 0
                            });
                            referencedFilesContent.push(merged);
                        } else {
                            console.warn(`[CHAT] ⚠️ No se pudo obtener contenido para ${file.original_name}`);
                        }
                    }
                }
            }

            console.log(`[CHAT] 📤 Archivos con contenido para enviar: ${referencedFilesContent.length}`);

            // Enviar la consulta al agente con streaming
            console.log('[DEBUG-SAVE] Antes de handleInvokeAgent');
            let agentResult = await handleInvokeAgent(finalQuery, id_conversation, id_message_bot, uploadedFile, referencedFilesContent);
            console.log('[DEBUG-SAVE] Después de handleInvokeAgent, result:', agentResult);

            // Extraer la respuesta del objeto retornado y limpiar marcadores :::TEXTO:::
            let responseBedrock = cleanAgentResponse(agentResult?.completion);
            console.log('[DEBUG-SAVE] responseBedrock:', responseBedrock?.substring(0, 100));

            // Limpiar archivo subido después de enviar
            if (uploadedFile) {
                handleFileClear();
            }

            // Limpiar archivos referenciados con @ después de enviar
            if (currentReferencedFiles && currentReferencedFiles.length > 0) {
                clearReferencedFiles();
            }

            if (responseBedrock === undefined || responseBedrock === null || responseBedrock === "Sorry, I am unable to assist you with this request.") {
                const claudeSuggestions = await consultClaude(
                    message,
                    "El servidor no pudo procesar esta consulta. Posiblemente sea demasiado compleja o contenga elementos no soportados."
                );

                if (claudeSuggestions) {
                    responseBedrock = `La consulta no puede procesarse en este momento. Aquí hay algunas alternativas que podrías intentar:\n\n${claudeSuggestions}\n\nSelecciona una de estas alternativas o reformula tu consulta de otra manera.`;
                } else {
                    responseBedrock = "Lo siento, no puedo procesar esta consulta en este momento. Intenta ser más específico o dividir tu pregunta en consultas más simples.";
                }
            }

            console.log('[DEBUG-SAVE] Antes de guardar mensaje del bot');
            console.log('[DEBUG-SAVE] Params:', { id_conversation, id_user: id_user?.substring(0, 20), responseBedrock: responseBedrock?.substring(0, 50), id_message_bot });
            let response_message_bot = await addMessageBD(id_conversation, id_user, responseBedrock, "Bot", id_message_bot);
            console.log('[DEBUG-SAVE] Mensaje del bot guardado:', response_message_bot);

            // 💾 GUARDAR SQL EN LA BASE DE DATOS para persistencia
            const capturedSQL = agentResult?.captured_sql;
            if (capturedSQL) {
                console.log('[PERSISTENCIA] Guardando SQL en BD:', capturedSQL.substring(0, 80) + '...');
                updateMessageBD(id_message_bot, capturedSQL).then(() => {
                    console.log('[PERSISTENCIA] SQL guardado exitosamente');
                }).catch(err => {
                    console.error('[PERSISTENCIA] Error guardando SQL:', err);
                });
            }

            // Limpiar el mensaje de streaming antes de agregar el mensaje final
            setStreamingMessage('');

            // 🔧 ELIMINAR MENSAJE PREVIEW Y AGREGAR RESPUESTA FINAL
            setMessages(prevMessages => {
                // Filtrar el mensaje preview
                const filteredMessages = prevMessages.filter(msg =>
                    !(msg.isPreviewMessage && (msg.id === firstAgentMessageId))
                );

                // Crear nuevo mensaje con la respuesta
                const newBotMessage = {
                    author: 'Bot',
                    id: bedrockMessageId,
                    id_message: id_message_bot,
                    text: responseBedrock,
                    type: 'text',
                    data: null,
                    temp: false,
                    isBedrockMessage: true,
                    isPreviewMessage: false,
                    input_tokens: agentResult?.input_tokens || 0,
                    output_tokens: agentResult?.output_tokens || 0,
                    content: (
                            <div>
                                {/* Mostrar contexto de imagen si existe */}
                                {hasImageProcessing && imageAnalysisContext &&
                                    !imageAnalysisContext.includes("Error") && (
                                        <div className="image-context-container">
                                            <details>
                                                <summary>
                                                    🔍 Información extraída de la imagen (click para ver)
                                                </summary>
                                                <div className="image-context-content">
                                                    <ReactMarkdown>{imageAnalysisContext}</ReactMarkdown>
                                                </div>

                                            </details>
                                        </div>
                                    )}

                                {/* Respuesta principal */}


                                <ReactMarkdown
                                    remarkPlugins={[remarkGfm]} // IMPORTANTE: Agregar esto
                                    components={{
                                        // Código existente
                                        code({ node, inline, className, children, ...props }) {
                                            const match = /language-(\w+)/.exec(className || '');
                                            return !inline && match ? (
                                                <SyntaxHighlighter
                                                    style={tomorrow}
                                                    language={match[1]}
                                                    PreTag="div"
                                                    {...props}
                                                >
                                                    {String(children).replace(/\n$/, '')}
                                                </SyntaxHighlighter>
                                            ) : (
                                                <code className={className} {...props}>
                                                    {children}
                                                </code>
                                            );
                                        },
                                        // NUEVO: Componentes para tablas
                                        // NUEVO: Componente para tablas simplificado
                                        table({ children }) {
                                            return <MarkdownTableWithDownload>{children}</MarkdownTableWithDownload>;
                                        },
                                        thead({ children }) {
                                            return <thead className="markdown-thead">{children}</thead>;
                                        },
                                        tbody({ children }) {
                                            return <tbody className="markdown-tbody">{children}</tbody>;
                                        },
                                        tr({ children }) {
                                            return <tr className="markdown-tr">{children}</tr>;
                                        },
                                        th({ children, style }) {
                                            return (
                                                <th className="markdown-th" style={style}>
                                                    {children}
                                                </th>
                                            );
                                        },
                                        td({ children, style }) {
                                            return (
                                                <td className="markdown-td" style={style}>
                                                    {children}
                                                </td>
                                            );
                                        },
                                        // Mantener los demás componentes existentes
                                        h1: ({ children }) => <h1 style={{ color: '#333', marginBottom: '10px' }}>{children}</h1>,
                                        h2: ({ children }) => <h2 style={{ color: '#555', marginBottom: '8px' }}>{children}</h2>,
                                        h3: ({ children }) => <h3 style={{ color: '#777', marginBottom: '6px' }}>{children}</h3>,
                                        p: ({ children }) => <p style={{ marginBottom: '10px', lineHeight: '1.6' }}>{children}</p>,
                                        ul: ({ children }) => <ul style={{ marginLeft: '20px', marginBottom: '10px' }}>{children}</ul>,
                                        ol: ({ children }) => <ol style={{ marginLeft: '20px', marginBottom: '10px' }}>{children}</ol>,
                                        li: ({ children }) => <li style={{ marginBottom: '5px' }}>{children}</li>,
                                        blockquote: ({ children }) => (
                                            <blockquote style={{
                                                borderLeft: '4px solid #ddd',
                                                paddingLeft: '15px',
                                                margin: '10px 0',
                                                fontStyle: 'italic',
                                                color: '#666'
                                            }}>
                                                {children}
                                            </blockquote>
                                        )
                                    }}
                                >
                                    {responseBedrock}
                                </ReactMarkdown>
                            </div>
                        )
                };

                // Agregar el nuevo mensaje al final
                return [...filteredMessages, newBotMessage];
            });

            // 🔧 SCROLL FINAL MEJORADO
            setTimeout(() => {
                scrollToBottom();
            }, 300);

            // 🔄 EJECUCIÓN SECUENCIAL: Visualización PRIMERO, luego Sugerencias
            // Esto evita que las peticiones se bloqueen entre sí
            const originalQuestion = finalQuery;
            // capturedSQL ya está definido arriba para guardar en BD

            // Función asíncrona para ejecutar secuencialmente
            (async () => {
                try {
                    // PASO 1: Ejecutar visualización SI hay SQL capturado
                    if (capturedSQL) {
                        console.log('[SECUENCIA] Paso 1: Iniciando visualización...');
                        setVizLoading(prev => ({ ...prev, [id_message_bot]: true }));
                        await loadVisualizationInBackground(id_message_bot, capturedSQL, finalQuery, responseBedrock);
                        console.log('[SECUENCIA] Paso 1: Visualización completada');
                    } else {
                        console.log('[SECUENCIA] Paso 1: Sin SQL capturado, omitiendo visualización');
                        // Marcar como "sin datos" pero no error
                        setVizData(prev => ({
                            ...prev,
                            [id_message_bot]: {
                                data: [],
                                columns: [],
                                column_types: {},
                                query: '',
                                hasError: false,
                                noAutoData: true
                            }
                        }));
                    }

                    // PASO 2: Generar sugerencias DESPUÉS de que visualización termine
                    console.log('[SECUENCIA] Paso 2: Iniciando sugerencias...');
                    await getSuggestedQuestions(originalQuestion, responseBedrock);
                    console.log('[SECUENCIA] Paso 2: Sugerencias completadas');

                    // PASO 3: Generar título si es el primer mensaje de la conversación
                    if (isFirstMessage && id_conversation) {
                        console.log('[SECUENCIA] Paso 3: Generando título de conversación...');
                        await generateConversationTitle(id_conversation, originalQuestion);
                        console.log('[SECUENCIA] Paso 3: Título generado');
                    }

                } catch (seqError) {
                    console.error('[SECUENCIA] Error en ejecución secuencial:', seqError);
                }
            })();

        } catch (error) {
            console.error('Error fetching data:', error);

            // Limpiar streaming message en caso de error
            setStreamingMessage('');

            try {
                const claudeSuggestions = await consultClaude(
                    message,
                    `Error técnico: ${error.message || "Error de conexión"}`
                );

                const errorMessage = claudeSuggestions
                    ? `Hubo un problema con la conexión al servidor. Mientras tanto, puedes intentar con estas alternativas:\n\n${claudeSuggestions}`
                    : "Hubo un problema con la conexión al servidor. Intenta con otra consulta o verifica tu conexión.";

                // 🔧 ELIMINAR PREVIEW Y AGREGAR ERROR
                setMessages(prevMessages => {
                    const filteredMessages = prevMessages.filter(msg =>
                        !(msg.isPreviewMessage && (msg.id === firstAgentMessageId))
                    );

                    const errorBotMessage = {
                        author: 'Bot',
                        id: bedrockMessageId,
                        content: errorMessage,
                        type: 'text',
                        temp: false,
                        isPreviewMessage: false
                    };

                    return [...filteredMessages, errorBotMessage];
                });
            } catch (claudeError) {
                setMessages(prevMessages => {
                    const filteredMessages = prevMessages.filter(msg =>
                        !(msg.isPreviewMessage && (msg.id === firstAgentMessageId))
                    );

                    const errorBotMessage = {
                        author: 'Bot',
                        id: bedrockMessageId,
                        content: "Hubo un problema con la conexión al servidor. Intenta con otra consulta o verifica tu conexión.",
                        type: 'text',
                        temp: false,
                        isPreviewMessage: false
                    };

                    return [...filteredMessages, errorBotMessage];
                });
            }
        }
    };

    // Interfaz
    return (
        <div className="chat-container">
            <div className="messages-box">
                <ul className="messages-list">
                    {messages.map((msg, index) => (
                        <li key={index} className={`message ${msg.author === 'Bot' ? 'received' : 'sent'}`}>
                            <div className="message-text">
                                {msg.author === 'Bot' && <BotStarsIcon className="bot-icon" />}
                                <div className="message-content">
                                    {/* Mostrar archivos adjuntos en mensajes del Cliente */}
                                    {msg.author === 'Client' && msg.attachments && (
                                        (msg.attachments.uploadedFile || msg.attachments.imagePreview ||
                                         (msg.attachments.referencedFiles && msg.attachments.referencedFiles.length > 0)) && (
                                            <div className="message-attachments">
                                                {/* Imagen adjunta */}
                                                {msg.attachments.imagePreview && (
                                                    <div className="msg-attachment-item msg-image-attachment">
                                                        <img src={msg.attachments.imagePreview} alt="Adjunto" className="msg-attachment-thumb" />
                                                        <span>{msg.attachments.uploadedFile?.filename || 'Imagen'}</span>
                                                    </div>
                                                )}
                                                {/* Archivo adjunto (PDF, Excel, etc) */}
                                                {!msg.attachments.imagePreview && msg.attachments.uploadedFile && (
                                                    <div className={`msg-attachment-item msg-${msg.attachments.uploadedFile.type}-attachment`}>
                                                        {msg.attachments.uploadedFile.type === 'pdf' ? <FaFilePdf size={14} /> :
                                                         msg.attachments.uploadedFile.type === 'excel' || msg.attachments.uploadedFile.type === 'csv' ? <FaFileExcel size={14} /> :
                                                         <FaFile size={14} />}
                                                        <span>{msg.attachments.uploadedFile.filename}</span>
                                                    </div>
                                                )}
                                                {/* Archivos referenciados con @ */}
                                                {msg.attachments.referencedFiles && msg.attachments.referencedFiles.map(file => (
                                                    <div key={file.id} className="msg-attachment-item msg-reference-attachment">
                                                        <span className="msg-ref-icon">@</span>
                                                        <span>{file.original_name}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )
                                    )}
                                    <div className={hoverButtonIndex === index ? "pre-with-padding" : ""}>
                                        {typeof msg.content === 'string' ? (
                                            // Busca TODOS los lugares donde tienes <ReactMarkdown> y reemplázalos con esta estructura:

                                            <ReactMarkdown
                                                remarkPlugins={[remarkGfm]} // IMPORTANTE: Agregar esto
                                                components={{
                                                    // Código existente
                                                    code({ node, inline, className, children, ...props }) {
                                                        const match = /language-(\w+)/.exec(className || '');
                                                        return !inline && match ? (
                                                            <SyntaxHighlighter
                                                                style={tomorrow}
                                                                language={match[1]}
                                                                PreTag="div"
                                                                {...props}
                                                            >
                                                                {String(children).replace(/\n$/, '')}
                                                            </SyntaxHighlighter>
                                                        ) : (
                                                            <code className={className} {...props}>
                                                                {children}
                                                            </code>
                                                        );
                                                    },
                                                    // NUEVO: Componentes para tablas
                                                    // ESTE SÍ está bien
                                                    table({ children }) {
                                                        return <MarkdownTableWithDownload>{children}</MarkdownTableWithDownload>;
                                                    },
                                                    thead({ children }) {
                                                        return <thead className="markdown-thead">{children}</thead>;
                                                    },
                                                    tbody({ children }) {
                                                        return <tbody className="markdown-tbody">{children}</tbody>;
                                                    },
                                                    tr({ children }) {
                                                        return <tr className="markdown-tr">{children}</tr>;
                                                    },
                                                    th({ children, style }) {
                                                        return (
                                                            <th className="markdown-th" style={style}>
                                                                {children}
                                                            </th>
                                                        );
                                                    },
                                                    td({ children, style }) {
                                                        return (
                                                            <td className="markdown-td" style={style}>
                                                                {children}
                                                            </td>
                                                        );
                                                    },
                                                    // Mantener los demás componentes existentes
                                                    h1: ({ children }) => <h1 style={{ color: '#333', marginBottom: '10px' }}>{children}</h1>,
                                                    h2: ({ children }) => <h2 style={{ color: '#555', marginBottom: '8px' }}>{children}</h2>,
                                                    h3: ({ children }) => <h3 style={{ color: '#777', marginBottom: '6px' }}>{children}</h3>,
                                                    p: ({ children }) => <p style={{ marginBottom: '10px', lineHeight: '1.6' }}>{children}</p>,
                                                    ul: ({ children }) => <ul style={{ marginLeft: '20px', marginBottom: '10px' }}>{children}</ul>,
                                                    ol: ({ children }) => <ol style={{ marginLeft: '20px', marginBottom: '10px' }}>{children}</ol>,
                                                    li: ({ children }) => <li style={{ marginBottom: '5px' }}>{children}</li>,
                                                    blockquote: ({ children }) => (
                                                        <blockquote style={{
                                                            borderLeft: '4px solid #ddd',
                                                            paddingLeft: '15px',
                                                            margin: '10px 0',
                                                            fontStyle: 'italic',
                                                            color: '#666'
                                                        }}>
                                                            {children}
                                                        </blockquote>
                                                    )
                                                }}
                                            >
                                                {msg.content}
                                            </ReactMarkdown>
                                        ) : (
                                            msg.content
                                        )}
                                    </div>

                                    {/* Botón para mensajes del bot (visualizar) */}
                                    {msg.author === 'Bot' && !msg.temp && !msg.isPreviewMessage && (
                                        <button
                                            onClick={() => handleViz(msg.id_message)}
                                            className="btn btn-visualize"
                                            disabled={vizLoading[msg.id_message]}
                                        >
                                            {vizLoading[msg.id_message] ? (
                                                <><FaSpinner className="fa-spin" style={{ marginRight: "5px" }} /> {t('chat.loadingData')}</>
                                            ) : (
                                                <><FaChartLine style={{ marginRight: "5px" }} /> {t('chat.visualize')}</>
                                            )}
                                        </button>
                                    )}

                                    {/* Opciones para mensajes del bot (3 puntos en hover, arriba) */}
                                    {msg.author === 'Bot' && !msg.temp && !msg.isPreviewMessage && (
                                        <div className="message-options-container received-options">
                                            <button
                                                className="message-options-btn"
                                                onClick={(e) => handleOptionsClick(index, e)}
                                                onMouseEnter={() => handleButtonMouseEnter(index)}
                                                onMouseLeave={handleButtonMouseLeave}
                                                title={t('chat.options')}
                                            >
                                                <FaEllipsisV size={14} />
                                            </button>

                                            {activeOptionsMenu === index && (
                                                <div className="message-options-menu" onClick={(e) => e.stopPropagation()}>
                                                    <button
                                                        className="message-option-item"
                                                        onClick={() => { openSaveToReport(msg); setActiveOptionsMenu(null); }}
                                                    >
                                                        <FaThumbtack style={{ marginRight: "8px" }} /> {t('chat.saveToReport')}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Opciones para mensajes del usuario */}
                                    {msg.author === 'Client' && (
                                        <div className="message-options-container">
                                            <button
                                                className="message-options-btn"
                                                onClick={(e) => handleOptionsClick(index, e)}
                                                onMouseEnter={() => handleButtonMouseEnter(index)}
                                                onMouseLeave={handleButtonMouseLeave}
                                                title={t('chat.options')}
                                            >
                                                <FaEllipsisV size={14} />
                                            </button>

                                            {activeOptionsMenu === index && (
                                                <div className="message-options-menu" onClick={(e) => e.stopPropagation()}>
                                                    <button
                                                        className="message-option-item"
                                                        onClick={() => handleSaveConsult(msg.content)}
                                                        disabled={savingConsult}
                                                    >
                                                        {savingConsult ? (
                                                            <><FaSpinner className="fa-spin" style={{ marginRight: "5px" }} /> {t('chat.saving')}</>
                                                        ) : (
                                                            <><FaBookmark style={{ marginRight: "5px" }} /> {t('chat.saveQuery')}</>
                                                        )}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>

                {/* Mensaje de pensamiento del agente */}
                {showThinking && thinkingMessage && (
                    <div className="thinking-message">
                        <div className="thinking-content">
                            <div className="thinking-icon">
                                <FaSpinner className="fa-spin" />
                            </div>
                            <div className="thinking-text">
                                <span className="thinking-label">Analizando...</span>
                                <span className="thinking-detail">{thinkingMessage}</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Mensaje en streaming */}
                {streamingMessage && (
                    <li className="message received streaming-message">
                        <div className="message-text">
                            <BotStarsIcon className="bot-icon" />
                            <div className="message-content">
                                <ReactMarkdown
                                    remarkPlugins={[remarkGfm]}
                                    components={{
                                        code({ node, inline, className, children, ...props }) {
                                            const match = /language-(\w+)/.exec(className || '');
                                            return !inline && match ? (
                                                <SyntaxHighlighter
                                                    style={tomorrow}
                                                    language={match[1]}
                                                    PreTag="div"
                                                    {...props}
                                                >
                                                    {String(children).replace(/\n$/, '')}
                                                </SyntaxHighlighter>
                                            ) : (
                                                <code className={className} {...props}>
                                                    {children}
                                                </code>
                                            );
                                        },
                                        p: ({ children }) => <p style={{ marginBottom: '10px', lineHeight: '1.6' }}>{children}</p>,
                                    }}
                                >
                                    {streamingMessage}
                                </ReactMarkdown>
                                <span className="streaming-cursor">▊</span>
                            </div>
                        </div>
                    </li>
                )}

                <div ref={messagesEndRef} />
            </div>

            <div className={`form-container`}>
                {/* Carrusel de preguntas sugeridas */}
                <div className="suggested-questions-carousel-container">
                    <div className="suggested-questions-carousel" ref={carouselRef}>
                        {loadingSuggestions ? (
                            <div className="loading-suggestions">
                                <FaSpinner className="fa-spin" style={{ marginRight: "5px" }} />
                                {t('chat.loadingSuggestions')}
                            </div>
                        ) : (
                            <div className="carousel-track">
                                {suggestedQuestions.map((question, idx) => (
                                    <div
                                        key={idx}
                                        className="suggested-question"
                                        onClick={() => handleSuggestedQuestion(question)}
                                    >
                                        {question}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Formulario con botón de búsqueda web */}
                <form className="message-form" onSubmit={handleSubmit}>
                    {/* BARRA DE ARCHIVOS UNIFICADA - Archivos adjuntos + Referencias @ */}
                    {((imagePreview || uploadedFile) || (referencedFiles && referencedFiles.length > 0)) && (
                        <div className="attachments-bar">
                            {/* Archivo adjunto (imagen, PDF, Excel) */}
                            {(imagePreview || uploadedFile) && (
                                <div className="attachment-item">
                                    {imagePreview ? (
                                        <div className="attachment-chip image-chip">
                                            <img src={imagePreview} alt="Preview" className="attachment-thumb" />
                                            <span className="attachment-name">{uploadedFile?.filename || 'Imagen'}</span>
                                            <button
                                                type="button"
                                                className="attachment-remove"
                                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); clearImage(); }}
                                            >
                                                <FaTimes size={10} />
                                            </button>
                                        </div>
                                    ) : uploadedFile && (
                                        <div className={`attachment-chip ${uploadedFile.type}-chip`}>
                                            <span className="attachment-icon">
                                                {uploadedFile.type === 'pdf' ? <FaFilePdf size={14} /> :
                                                 uploadedFile.type === 'excel' || uploadedFile.type === 'csv' ? <FaFileExcel size={14} /> :
                                                 <FaFile size={14} />}
                                            </span>
                                            <span className="attachment-name">
                                                {uploadedFile.filename?.length > 20
                                                    ? uploadedFile.filename.substring(0, 17) + '...'
                                                    : uploadedFile.filename}
                                            </span>
                                            <button
                                                type="button"
                                                className="attachment-remove"
                                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleFileClear(); }}
                                            >
                                                <FaTimes size={10} />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Archivos referenciados con @ */}
                            {referencedFiles && referencedFiles.length > 0 && referencedFiles.map(file => (
                                <div key={file.id} className="attachment-chip reference-chip">
                                    <span className="attachment-icon reference-icon">@</span>
                                    <span className="attachment-name">{file.original_name}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* botón adjuntar archivos (imágenes, Excel, CSV, PDF) */}
                    <input
                        type="file"
                        accept="image/*,.xlsx,.xls,.xlsm,.csv,.pdf"
                        ref={imageInputRef}
                        style={{ display: 'none' }}
                        onChange={handleImageChange}
                    />

                    {userFeatures.fileUpload !== false && (
                    <div className="clip-container">
                        <button
                            type="button"
                            className="clip-button tooltip"
                            data-tooltip="Adjuntar archivo"
                            onClick={(e) => {
                                e.preventDefault();
                                imageInputRef.current?.click();
                            }}
                            title="Adjuntar archivo (imagen, Excel, CSV, PDF)"
                            disabled={loading}
                        >
                            <FaPaperclip />
                        </button>
                    </div>
                    )}

                    {/* Dropdown de referencias de archivos @ */}
                    {userFeatures.fileUpload !== false && (
                    <FileReferenceDropdown
                        isOpen={showFileDropdown}
                        files={availableFiles}
                        searchText={fileSearchText}
                        selectedIndex={selectedFileIndex}
                        onSelect={handleFileReferenceSelect}
                        onClose={() => setShowFileDropdown(false)}
                    />
                    )}

                    {/* Textarea simple */}
                    <textarea
                        ref={messageInputRef}
                        className="message-input"
                        placeholder={t('chat.placeholder')}
                        value={message}
                        onChange={handleTextareaChange}
                        rows={1}
                        onKeyDown={e => {
                            // Primero verificar navegación del dropdown
                            handleTextareaKeyDown(e);
                            // Si no fue manejado por el dropdown, verificar Enter para enviar
                            if (e.key === 'Enter' && !e.shiftKey && !showFileDropdown) {
                                e.preventDefault();
                                handleSubmit(e);
                            }
                        }}
                    />

                    <button type="submit" className="btn btn-send"><FaArrowUp /></button>
                </form>

                <p className="disclaimer">
                    {t('chat.disclaimer')}
                </p>
            </div>

            {/* NUEVO: Modal de datos con tabla y exportacion */}
            {dataModalOpen && dataModalMessageId && (
                <div className="data-modal-overlay" onClick={handleCloseDataModal}>
                    <div className="data-modal-container" onClick={(e) => e.stopPropagation()}>
                        <DataTableExport
                            key={dataModalMessageId}
                            data={vizData[dataModalMessageId]?.data || []}
                            metadata={{
                                columns: vizData[dataModalMessageId]?.columns || [],
                                column_types: vizData[dataModalMessageId]?.column_types || {},
                                total_rows: vizData[dataModalMessageId]?.data?.length || 0
                            }}
                            sql={vizData[dataModalMessageId]?.query || ''}
                            explanation={vizData[dataModalMessageId]?.explanation || ''}
                            isLoading={vizLoading[dataModalMessageId]}
                            error={vizData[dataModalMessageId]?.hasError ? vizData[dataModalMessageId]?.error : null}
                            currentLimit={vizData[dataModalMessageId]?.currentLimit || 1000}
                            onReload={(newLimit) => reloadVisualizationData(dataModalMessageId, newLimit)}
                            onClose={handleCloseDataModal}
                            canExport={userFeatures.dataExport !== false}
                            onSaveChartToReport={openSaveChartToReport}
                            originalQuery={(() => {
                                const idx = messages.findIndex(m => m.id_message === dataModalMessageId);
                                return idx > 0 && messages[idx - 1]?.author === 'Client'
                                    ? (messages[idx - 1].content || '')
                                    : '';
                            })()}
                            username={id_user}
                            initialAiSuggestion={vizData[dataModalMessageId]?.aiSuggestion || null}
                            onAiSuggestionChange={(suggestion) => {
                                // 1) Memoria (cerrar/abrir modal en la misma sesión)
                                setVizData(prev => ({
                                    ...prev,
                                    [dataModalMessageId]: {
                                        ...(prev[dataModalMessageId] || {}),
                                        aiSuggestion: suggestion,
                                    }
                                }));
                                // 2) BD: persistir para que sobreviva al recargar
                                // la página. Fire-and-forget; no bloquea el render.
                                if (dataModalMessageId) {
                                    fetch(`${STRANDS_API_URL}/update_chart_suggestion`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({
                                            id_message: dataModalMessageId,
                                            chart_suggestion: suggestion,
                                        })
                                    }).catch(err => {
                                        console.warn('[CHAT] No se pudo persistir chart_suggestion:', err);
                                    });
                                }
                            }}
                        />
                    </div>
                </div>
            )}

            {/* Modal "Guardar en reporte" (cherry-pick) */}
            {saveReportModal && createPortal(
                <div className="save-report-overlay" onClick={closeSaveToReport}>
                    <div className="save-report-modal" onClick={e => e.stopPropagation()}>
                        <div className="save-report-header">
                            <h3>
                                {saveReportModal.pendingBlock?.type === 'chart'
                                    ? (t('chat.saveChartToReportTitle') || 'Guardar gráfica en reporte')
                                    : t('chat.saveToReportTitle')}
                            </h3>
                            <button className="save-report-close" onClick={closeSaveToReport}><FaTimes /></button>
                        </div>
                        {saveReportModal.pendingBlock?.type === 'chart' && saveReportModal.pendingBlock?.content?.image_data_url && (
                            <div className="save-report-chart-preview">
                                <img src={saveReportModal.pendingBlock.content.image_data_url} alt="Preview" />
                            </div>
                        )}
                        <button
                            className="save-report-new"
                            onClick={saveToNewReport}
                            disabled={savingToReport}
                        >
                            <FaPlus /> {saveReportModal.pendingBlock?.type === 'chart'
                                ? (t('chat.createNewReportWithChart') || 'Crear nuevo reporte con esta gráfica')
                                : t('chat.createNewReportWithMessage')}
                        </button>
                        <div className="save-report-divider">{t('chat.orAddToExisting')}</div>
                        <div className="save-report-list">
                            {loadingReportsForSave ? (
                                <div className="save-report-empty"><FaSpinner className="fa-spin" /> {t('chat.loadingReports')}</div>
                            ) : reportsForSave.length === 0 ? (
                                <div className="save-report-empty">{t('chat.noReportsYet')}</div>
                            ) : (
                                reportsForSave.map(rep => (
                                    <button
                                        key={rep.id}
                                        className="save-report-item"
                                        onClick={() => saveToExistingReport(rep)}
                                        disabled={savingToReport}
                                    >
                                        <FaFileAlt className="save-report-icon" />
                                        <span className="save-report-item-title">{rep.title}</span>
                                        <span className="save-report-item-count">{rep.block_count} {t('chat.blocksCount')}</span>
                                    </button>
                                ))
                            )}
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Toast "Guardado en reporte" */}
            {savedToReportToast && createPortal(
                <div className="saved-report-toast">
                    <FaCheck className="toast-check" />
                    <span>{t('chat.savedInReport')} <strong>{savedToReportToast.reportTitle}</strong></span>
                    <button
                        className="toast-link"
                        onClick={() => { navigate(`/report/${savedToReportToast.reportId}`); setSavedToReportToast(null); }}
                    >
                        {t('chat.view')}
                    </button>
                </div>,
                document.body
            )}

            {/* Modal de gráfica para tablas markdown */}
            {chartModalOpen && chartModalData && (
                <ChartModalWithSave
                    chartModalData={chartModalData}
                    onClose={() => setChartModalOpen(false)}
                    onSave={openSaveChartToReport}
                    t={t}
                />
            )}
        </div>
    );
};

// ─── Modal de chart para tablas markdown del agente ─────
// El botón "Guardar en reporte" vive AHORA dentro del header de
// DataTableExport (contextual: guarda tabla o gráfica según la tab),
// así que este componente solo provee el contenedor + la X.
function ChartModalWithSave({ chartModalData, onClose, onSave, t }) {
    return (
        <div className="chart-modal-overlay" onClick={onClose}>
            <div className="chart-modal-container" onClick={(e) => e.stopPropagation()}>
                <div className="chart-modal-header">
                    <h3>{t('chat.chartModalTitle')}</h3>
                    <div className="chart-modal-header-actions">
                        <button className="chart-modal-close" onClick={onClose}>
                            <FaTimes />
                        </button>
                    </div>
                </div>
                <div className="chart-modal-content">
                    <DataTableExport
                        data={chartModalData.data || []}
                        metadata={{
                            columns: chartModalData.headers || [],
                            column_types: {},
                            total_rows: (chartModalData.data || []).length,
                        }}
                        sql=""
                        explanation=""
                        isLoading={false}
                        error={null}
                        currentLimit={null}
                        onReload={null}
                        onClose={null}
                        canExport={true}
                        onSaveChartToReport={onSave}
                    />
                </div>
            </div>
        </div>
    );
}

export default Chatbotui;